import moved from "./en.moved";
import builder from "./en.builder";
import builtins from "./en.builtins";
import generic from "./en.generic";
import pages from "./en.pages";
import decisionControls from "./en.decisions";

export default {
  ...moved,
  ...builtins,
  ...generic,
  ...pages,
  builder,
  decisionControls,
  app: {
    title: "Platform Console",
    subtitle: "Configure services, workflows, agents and integrations of the National Competition Platform – without code.",
    openBackoffice: "Open Case Management",
    openPortal: "Open Services Portal",
    version: "Platform {{version}}",
    help: "Help",
    docs: "Administrator guide",
  },
  login: {
    title: "Platform Console sign-in",
    subtitle: "Administrative access is restricted to platform administrators on the Ministry's federal network.",
    noAccess: "This identity has no platform administration permissions. Sign in with an administrator account.",
    sessionNote: "Every session needs your password and an authenticator code. Sessions end after 30 minutes without activity and after 10 hours at most.",
    capabilities: {
      services: "Design services and intake forms",
      workflows: "Version workflows with pinned running cases",
      agents: "Configure agents, prompts and guardrails",
      integrations: "Connect government systems and identity providers",
    },
  },
  nav: {
    data: "Data",
    reports: "Reports",
    screens: "Screens",
    tools: "Agent tools",
    environments: "Environments",
    overview: "Overview",
    build: "Build",
    services: "Services",
    forms: "Forms",
    workflows: "Workflows",
    rules: "Rules",
    automations: "Automations",
    aiWorkspace: "AI Workspace",
    workspaces: "Workspaces",
    pages: "Page Studio",
    assistants: "Assistants",
    knowledge: "Knowledge",
    solutions: "Solutions",
    ai: "AI",
    agents: "Agents",
    models: "Models & Providers",
    governance: "AI Governance",
    connect: "Connect",
    integrations: "Integrations",
    connectors: "Connectors",
    webhooks: "Webhooks",
    apiClients: "API clients",
    identity: "Identity & Access",
    identityProviders: "Identity providers",
    users: "Users",
    roles: "Roles & permissions",
    content: "Templates & Content",
    operate: "Operate",
    settings: "Settings",
    flags: "Feature flags",
    secrets: "Secrets",
    releases: "Releases",
    packages: "Packages",
    audit: "Audit & Security",
    feedback: "Reported issues",
  },
  shell: {
    breadcrumbs: "Breadcrumbs",
    searchPlaceholder: "Search the console…",
    commandPalette: "Command palette",
    commandHint: "Type a command or search…",
    noResults: "Nothing matches.",
    groups: { navigate: "Go to", actions: "Actions", recent: "Recent", definitions: "Definitions" },
    shortcuts: "Keyboard shortcuts",
    shortcutList: {
      workspaces: "Go to Workspaces",
      solutions: "Go to Solutions",
      palette: "Open the command palette",
      search: "Focus search",
      overview: "Go to Overview",
      services: "Go to Services",
      workflows: "Go to Workflows",
      releases: "Go to Releases",
      help: "Show this list",
      save: "Save the current draft",
    },
    unsavedTitle: "Unsaved changes",
    unsavedBody: "You have changes that are not saved yet. Leave this page and discard them?",
    stay: "Stay",
    discard: "Discard changes",
    environment: "Environment",
    savedViews: "Saved views",
    saveView: "Save current view",
    viewName: "View name",
    deleteView: "Delete view",
    noSavedViews: "No saved views yet.",
    goToBackoffice: "Open in Case Management",
    signOut: "Sign out",
    help: "Help & documentation",
    helpHint: "What this screen does, how changes are released, and where to read more.",
  },
  overview: {
    title: "Platform overview",
    subtitle: "Health of the platform components, the agents that behave as services, the connected systems and everything waiting for a release decision.",
    health: "Platform health",
    healthHint: "Every component the workflow engine depends on. A failing agent or connector never blocks the engine: cases keep moving, only the assisted step is delayed.",
    components: { api: "API", database: "PostgreSQL", storage: "Object storage", jobs: "Background jobs", realtime: "Realtime hub", llm: "Model gateway" },
    status: { Healthy: "Healthy", Degraded: "Degraded", Down: "Down", Unknown: "Unknown" },
    queues: "Job queues",
    queuesHint: "Agents run on the `agents` queue after each transaction commits; SLA sweeps and notifications on `default`.",
    queue: { name: "Queue", enqueued: "Waiting", processing: "Running", failed: "Failed", succeeded24h: "Done (24h)" },
    agents: "Agents as services",
    agentsHint: "Each agent runs in isolation with its own budget, guardrails and retries. Success rate and latency over the last 24 hours.",
    agent: { name: "Agent", runs: "Runs", success: "Success", p95: "p95", lastFailure: "Last failure", never: "never" },
    connectors: "Connected systems",
    connectorsHint: "Outbound integrations with circuit breaker state and the last probe.",
    connector: { name: "System", health: "Health", breaker: "Breaker", latency: "Latency", lastCall: "Last call" },
    breaker: { Closed: "Closed", Open: "Open", HalfOpen: "Half-open" },
    providers: "Model providers",
    providersHint: "Where inference runs today and the data residency each provider offers.",
    approvals: "Waiting for release",
    approvalsHint: "Change sets that need a second pair of eyes before they take effect.",
    noApprovals: "Nothing is waiting for approval.",
    recentChanges: "Recent platform changes",
    recentChangesHint: "Published change sets, activations and configuration edits from the audit chain.",
    drift: "Evaluation drift",
    driftHint: "Alerts raised when an evaluation dataset lost more than ten accuracy points.",
    noDrift: "No drift alerts.",
    spend: "Model spend",
    spendHint: "Cost of model calls over the last 30 days, by tier.",
    runningCases: "Running cases by workflow version",
    runningCasesHint: "Cases keep the workflow version they started on. Older versions retire when their last case closes.",
    openConsole: "Open",
    viewAll: "View all",
    refreshed: "Refreshed {{when}}",
    workspaces: "Workspaces",
    workspacesHint: "Adoption of the AI Workspace per department: conversations and active staff this week, spend against the monthly budget and what awaits review.",
    workspace: {
      summary: "{{assistants}} assistants · {{members}} members",
      pending: "{{count}} to review",
      threads7d: "Threads · 7d",
      activeUsers: "Active · 7d",
      runs: "Agent runs · month",
      threadsTotal: "{{count}} in total",
      membersTotal: "of {{count}} members",
      artifacts: "{{count}} artifacts",
      spend: "Spend this month",
    },
  },
  releases: {
    title: "Releases",
    subtitle: "Every change to a service, form, workflow, rule, agent, connector, role, flag or setting goes live through a change set: reviewed by the advisor, approved by a second administrator, optionally scheduled, and reversible.",
    newRelease: "New release",
    newReleaseHint: "A release groups the definition versions that should go live together. Add items from the builders with “Add to release”.",
    created: "Release created.",
    empty: "No releases match.",
    emptyHint: "Create a release, then add items from the Workflow Studio, Form Builder and other builders.",
    searchPlaceholder: "Search by title, requester or definition key…",
    allStatuses: "All statuses",
    tabs: { open: "Open", history: "History", all: "All" },
    columns: { title: "Release", items: "Items", risk: "Risk", requestedBy: "Requested by", effective: "Effective" },
    approvedBy: "approved by {{name}}",
    immediate: "On approval",
    isRollback: "Rollback release",
    pendingHint: "Need a second administrator.",
    nextScheduled: "Next: {{title}} at {{when}}",
    noScheduled: "Nothing scheduled.",
    lastPublished: "Last: {{title}} by {{by}}",
    noPublished: "Nothing published yet.",
    rollbackHint: "Restored previous versions.",
    backToQueue: "Back to releases",
    detailSubtitle: "Requested by {{by}} · {{when}}",
    items: "Items",
    itemsHint: "Definition versions in this release, applied in order. Select one to see its diff.",
    noItems: "No items yet.",
    addFromBuilders: "Add items from the builders: open a draft in the Workflow Studio, Form Builder, Rule Library or Agent Builder and choose “Add to release”.",
    removeItem: "Remove",
    runningCases: "{{count}} running case(s)",
    advisor: "Advisor review",
    advisorHint: "Deterministic review of every item (diff, running cases, risk by change kind) attached when the release is submitted. Workflow items also get the Workflow Change Advisor's LLM review, linked below when it lands.",
    noReview: "The advisor review is attached when the release is submitted.",
    rerunReview: "Re-run review",
    llmAdvisories: "Workflow Change Advisor",
    evaluation: {
      title: "Agent go-live tests",
      hint: "Each agent version in this release must pass its test cases and the red-team pack before it can be approved.",
      score: "{{passed}}/{{total}} cases · {{red}}/{{redTotal}} red-team · {{by}} · {{when}}",
      never: "Not evaluated yet",
      blockedTitle: "Approval is blocked",
      blockedBody: "These agent versions have not passed their go-live tests: {{agents}}. Open the agent, run the evaluation and approve once it passes.",
    },
    // Condition 3.7 of the substantive review: a change to a model or its instructions counts as an update under condition 1.5.
    aiUpdate: {
      title: "AI update (conditions 1.5 and 3.7)",
      body: "This release changes a model or an agent's instructions: {{items}}. It may go to the test environment now; it reaches production only after every reference file and Part Six scenario passes and with the Department's written approval.",
      bodyRequired: "This release changes a model or an agent's instructions: {{items}}. On this installation it is approved only with the Department's written approval and the run of the reference files and Part Six scenarios that passed on it.",
      departmentApproval: "The Department's written approval",
      departmentApprovalHint: "Reference and date of the Department's letter or e-mail approving the change.",
      referenceRun: "Reference-file run",
      referenceRunHint: "The run of the reference files and Part Six scenarios on this change, and its result.",
    },
    gate: {
      title: "Production release (condition 1.5)",
      body: "On this installation no change goes live without the Department's written approval, given after the change passed every reference file and Part Six scenario. An urgent security fix may go with the approval alone and must pass the full set within five working days.",
      urgent: "Urgent security fix",
      urgentHint: "Approved with the Department's approval alone; the run of every reference file and Part Six scenario is due within five working days and is recorded on the release.",
      urgentRunsTitle: "Urgent security fix: the full set is due",
      runDue: "{{title}}: the run of every reference file and Part Six scenario is due by {{date}}.",
      runOverdue: "{{title}}: the run of every reference file and Part Six scenario was due by {{date}} and is not recorded.",
      recordRun: "Record the reference-file run",
      recordRunHint: "The reference and result of the run of every reference file and Part Six scenario on this urgent security fix.",
      runRecorded: "Reference-file run recorded on the release.",
      installationTitle: "The version this installation runs (condition 1.5)",
      installationApproved: "Version {{version}}, approved by the Department ({{approval}}) on the reference-file run {{run}}.",
      installationUrgent: "Version {{version}}, an urgent security fix approved by the Department ({{approval}}); its run of the full set is due by {{due}}.",
      installationNotApproved: "Version {{version}} has no approval of the Department on record; a production installation does not start on it.",
    },
    fourEyesTitle: "Waiting for a second administrator",
    fourEyesBody: "You requested this release, so someone else with the approve permission has to approve it (four-eyes rule).",
    scheduledTitle: "Scheduled",
    scheduledBody: "This release is approved and will be published automatically at {{when}}. Publish now, reopen or reject it before then if needed.",
    approveHint: "Approving publishes the release immediately. Add an optional note for the audit chain.",
    approveScheduledHint: "Approving schedules the release for {{when}}; the scheduler publishes it automatically.",
    rejectHint: "The requester can amend the release and resubmit it.",
    rollbackConfirm: "A rollback release restores the previously published version of every item, in reverse order. Running cases are not affected.",
    cancelHint: "The release is withdrawn; the definitions stay as drafts.",
    fields: { title: "Title", effectiveFrom: "Effective from", effectiveFromHint: "Leave empty to publish on approval. A future date schedules the release.", reason: "Reason / note" },
    actions: { submit: "Submit for approval", approve: "Approve", reject: "Reject", publishNow: "Publish now", reopen: "Reopen as draft", cancel: "Cancel release", rollback: "Roll back", addToRelease: "Add to release", stageAndOpen: "Added to release “{{title}}”" },
    toasts: { submitted: "Submitted for approval.", approved: "Approved.", scheduled: "Approved; it is published on its effective date.", rejected: "Rejected.", published: "Published.", reopened: "Reopened as draft.", cancelled: "Release cancelled.", rolledBack: "Rolled back.", reviewed: "Advisor review refreshed.", itemRemoved: "Item removed." },
    // A publish, approval or rollback that ran and failed (S 2.8): the heading of the failure, the cause and the server's words follow.
    failed: { publish: "Not published: the release failed and is marked Failed", approve: "Approved, but not published: the release failed and is marked Failed", rollback: "Not rolled back: the release stays as it was published" },
    timeline: { created: "Release created", submitted: "Submitted for approval", advisor: "Advisor risk: {{risk}}", approved: "Approved (four eyes)", selfApproved: "Approved by the requester", rejected: "Rejected", scheduled: "Scheduled", published: "Published", failed: "Publish failed", rolledBack: "Rolled back", cancelled: "Cancelled" },
    status: { Draft: "Draft", PendingApproval: "Pending approval", Approved: "Approved", Scheduled: "Scheduled", Published: "Published", Rejected: "Rejected", RolledBack: "Rolled back", Failed: "Failed", Cancelled: "Cancelled" },
    risk: { Minimal: "Minimal risk", Low: "Low risk", Medium: "Medium risk", High: "High risk" },
    kinds: { Publish: "Publish", Retire: "Retire", Setting: "Setting", Flag: "Flag", Role: "Role" },
    types: { service: "Service", form: "Form", rule: "Rule", automation: "Automation", agent: "Agent", connector: "Connector", workflow: "Workflow", setting: "Setting", flag: "Flag", role: "Role", template: "Template", identityProvider: "Identity provider", llmProvider: "LLM provider", fee: "Fee schedule", analysisPolicy: "Calculation method" },
    diff: { patch: "Changes", before: "Before", after: "After", firstVersion: "First published version – there is nothing to compare with.", noChanges: "No structural difference from the published version.", nothing: "—" },
    help: {
      what: "Releases are change sets: bundles of definition versions (workflows, forms, rules, agents, connectors, services) plus role, flag and setting changes that go live together.",
      fourEyes: "The requester submits; the advisor attaches a risk review; a different administrator approves. Releases with a future effective date are published by the scheduler.",
      rollback: "A published release can be rolled back as long as no later release touched the same definitions. The rollback is itself a release, so the history stays complete.",
    },
  },
  packages: {
    title: "Packages",
    subtitle: "Solution packages move configuration between deployments: export services, workflows, forms, rules, agents, connectors, templates, roles, flags and settings as one checksummed file; import it on a blank platform as a draft release.",
    exportPackage: "Export package",
    importPackage: "Import package",
    gallery: "Gallery",
    galleryHint: "Start a new deployment from a template or lift this department out as a package.",
    galleryKind: { builtin: "Built-in template", package: "Template package" },
    exportTemplate: "Export as package",
    exporting: "Exporting…",
    stored: "Packages",
    storedHint: "Exported, uploaded and imported packages on this platform.",
    templates: "Templates",
    templatesHint: "Gallery entries ready to install.",
    lastExport: "Last export",
    lastImport: "Last import",
    nothingYet: "Nothing yet.",
    byAt: "{{name}} · {{when}}",
    empty: "No packages yet.",
    emptyHint: "Export the Competition Department from the gallery, or import a package file to start.",
    columns: { package: "Package", items: "Items", size: "Size", provenance: "Provenance", changeSet: "Release" },
    status: { Draft: "Uploaded", Exported: "Exported", Imported: "Imported", Failed: "Failed" },
    exportedBy: "Exported by {{name}}",
    importedBy: "Imported by {{name}}",
    uploaded: "Uploaded, not yet imported",
    openRelease: "Open release",
    noRelease: "—",
    itemsByType: "{{count}} {{type}}",
    wizard: {
      title: "Export a solution package",
      hint: "Pick the definitions to include. Dependencies (the form a service uses, the roles a workflow references…) are pulled in automatically.",
      steps: { details: "Details", items: "Definitions", review: "Review" },
      key: "Package key",
      keyHint: "Lowercase identifier used in file names, e.g. moet-competition-department.",
      publisher: "Publisher",
      resolveDeps: "Include dependencies",
      resolveDepsHint: "Add the definitions the selected ones reference so the package installs cleanly on a blank platform.",
      isTemplate: "Show in the gallery as a template",
      selectAllType: "All {{type}}",
      selected: "{{count}} selected",
      searchItems: "Filter definitions…",
      noItemsOfType: "None published.",
      reviewHint: "This is what the package will contain. Items pulled in as dependencies show what required them.",
      requiredBy: "required by {{by}}",
      warnings: "Warnings",
      exportNow: "Export",
      exported: "Package exported.",
      nothingSelected: "Select at least one definition.",
    },
    upload: {
      title: "Import a solution package",
      hint: "Upload a .ncp-package.json file or paste its contents. The package is validated (checksums, dependencies) and previewed before anything is created – and even then only drafts in a release.",
      drop: "Drop a package file here or click to choose",
      paste: "Or paste the package JSON",
      pasteHint: "Paste the full document including the manifest.",
      uploadAction: "Validate",
      uploaded: "Package uploaded and validated.",
      invalidJson: "The pasted text is not valid JSON.",
    },
    detail: {
      back: "Back to packages",
      manifest: "Manifest",
      manifestHint: "Every definition in the package with its checksum. When the package is planned against this platform, each row shows what importing it would do.",
      plan: "Import plan",
      planHint: "Validation of the file against this platform: checksums, format, dependencies and per-item actions. Nothing is written until you import.",
      planning: "Planning…",
      checksumOk: "All checksums verified",
      checksumFailed: "Checksum failure – the file was modified after export",
      format: "Format {{format}} · exported from platform {{platform}}",
      missingDeps: "Missing on this platform",
      missingDepsHint: "Referenced by items in the package but neither included nor present here.",
      import: "Import into a draft release",
      importHint: "Selected create/update items become drafts (new versions) collected in one draft release. Roles and flags are created disabled/without permissions; the release grants them.",
      releaseTitle: "Release title",
      submit: "Submit the release for approval right away",
      importNow: "Import {{count}} item(s)",
      nothingToImport: "Everything selected is already on this platform.",
      imported: "Package imported into a draft release.",
      partial: "Imported with {{count}} failure(s).",
      result: "Import result",
      outcome: { staged: "Staged in release", unchanged: "Unchanged", skipped: "Skipped", failed: "Failed", create: "Not selected", update: "Not selected" },
      openChangeSet: "Open the release",
      download: "Download",
      delete: "Delete package",
      deleteConfirm: "Delete this package file? Imported packages are kept for provenance.",
      deleted: "Package deleted.",
      sha: "SHA-256",
      size: "Size",
      created: "Created",
      platform: "Platform",
      dependencies: "External dependencies",
      dependenciesHint: "Expected to exist on the target platform.",
      action: { create: "Create", update: "New version", unchanged: "Unchanged", unsupported: "Unsupported", invalid: "Invalid" },
      current: "here: v{{version}}",
      selectImportable: "Select all importable",
      provenance: "Provenance",
      inStorage: "Stored in object storage",
      inDatabase: "Stored in the database",
      importedInto: "Imported into release",
    },
    help: {
      what: "A solution package is a manifest plus the canonical JSON of each definition, each with a SHA-256 checksum. “Competition Department” is the package that turns a blank platform into this deployment; a DED exports its own the same way.",
      import: "Importing never changes the running platform directly: every item becomes a draft version collected in one release, which then goes through advisor review and four-eyes approval.",
      secrets: "Secrets are never exported – connectors and providers carry secret:// references only, and the target platform provides the values.",
    },
  },
  settings: {
    title: "Settings & Flags",
    subtitle: "Schema-driven system settings, feature flags with targeting, and the encrypted secret store. Settings that shape statutory behaviour change through a release; the rest apply immediately and are audited.",
    tabs: { settings: "Settings", flags: "Feature flags", secrets: "Secrets" },
    groups: "Groups",
    overridden: "{{count}} changed",
    showAdvanced: "Show advanced",
    default: "Default",
    changed: "Changed",
    release: "Release",
    releaseHint: "Changes to this setting go through a release (four-eyes approval).",
    restart: "Restart",
    restartHint: "The API restarts before the new value is used.",
    resetToDefault: "Reset to default",
    resetDone: "Reset to the default value.",
    saved: "Setting saved.",
    staged: "Change added to the draft release.",
    testEnvironmentLine: "Test environment – decision controls relaxed, not production behaviour:",
    testEnvironmentCodes: {
      Registers: "registers warn instead of blocking a signature",
      Heads: "the Director and above take every act and sign every decision",
      Penalty: "penalty steps open before the basis is confirmed",
      Letters: "letters citing unverified provisions are not held for that alone",
    },
    openRelease: "Open release",
    invalidJson: "Not valid JSON.",
    defaultIs: "Default: {{value}}",
    updatedAt: "Changed {{when}}",
    jsonEditor: "JSON value",
    noSettings: "No settings in this group.",
    help: {
      what: "Settings are described by a schema (group, type, options, default) so the console can render, validate and diff them. Values live in system_settings, where existing code already reads them.",
      release: "Settings marked Release never change directly: saving stages a change-set item that a second administrator approves. Everything else applies at once and is written to the audit chain.",
      secrets: "Secrets are encrypted with ASP.NET Data Protection and referenced from connectors, providers and webhooks as secret://key. The console shows names, hints and where they are used – never the value.",
    },
    flags: {
      title: "Feature flags",
      hint: "Flags gate the strangler migration (portal.dynamicIntake.<service>) and every new console capability. Toggle is immediate; targeting lets you enable a flag for roles or people before everyone.",
      newFlag: "New flag",
      editFlag: "Edit flag",
      known: "Used by code",
      knownHint: "Referenced by platform code; can be disabled but not deleted.",
      custom: "Custom",
      on: "On",
      off: "Off",
      targeted: "Targeted",
      percentage: "{{value}}% rollout",
      roles: "Roles: {{roles}}",
      users: "{{count}} people",
      expires: "Expires {{when}}",
      expired: "Expired",
      categories: { portal: "Portal", backoffice: "Back office", console: "Console", agents: "Agents", automations: "Automations", integrations: "Integrations", identity: "Identity", experiments: "Experiments", general: "General" },
      fields: {
        key: "Key", keyHint: "Dotted lowercase key, e.g. portal.dynamicIntake.lq.", category: "Category", enabled: "Enabled for everyone", enabledHint: "When off, only targeted roles and people see the feature.",
        roles: "Enabled for roles", rolesHint: "Members of these roles always get the feature.", users: "Enabled for people", usersHint: "User ids, one per line or comma-separated.",
        percentage: "Percentage rollout", percentageHint: "Stable per person: the same user stays on the same side while the percentage grows.", expiresAt: "Expires at", expiresHint: "Optional reminder to clean the flag up.",
      },
      toasts: { enabled: "Flag enabled.", disabled: "Flag disabled.", saved: "Flag saved.", created: "Flag created.", deleted: "Flag deleted." },
      deleteConfirm: "Delete this flag? Code that still asks for it will read it as off.",
      empty: "No flags yet.",
    },
    secrets: {
      title: "Secrets",
      hint: "Values are write-only: once stored you see a hint (the last characters) and where the secret is referenced. Rotate by entering a new value.",
      newSecret: "New secret",
      rotate: "Rotate",
      rotateTitle: "Rotate secret",
      test: "Test",
      testOk: "Secret decrypts ({{length}} characters).",
      testFailed: "Secret could not be read.",
      reference: "Reference",
      referenceHint: "Paste this into a connector, provider or webhook field.",
      usedBy: "Used by",
      unused: "Not referenced",
      rotated: "Rotated {{when}} by {{name}}",
      neverRotated: "Never rotated",
      expires: "Expires {{when}}",
      expired: "Expired",
      fromEnv: "Environment variable",
      envMissing: "not set",
      envPresent: "present",
      fields: {
        key: "Key", keyHint: "Dotted lowercase key, e.g. eia.apiKey.", value: "Value", valueHint: "Stored encrypted. Leave empty when only changing the description or expiry.", newValue: "New value",
        description: "Description", expiresAt: "Expires at", env: "Environment variable (bootstrap)", envHint: "Read the value from this variable at runtime instead of the stored cipher text – for secrets provisioned by the deployment.",
      },
      dangling: "Missing secrets",
      danglingHint: "Configuration references these keys but no secret is defined – the referencing connector or provider will fail.",
      createMissing: "Create",
      deleteConfirm: "Delete this secret? Anything referencing it will fail to authenticate.",
      forceDelete: "Delete anyway",
      inUse: "This secret is referenced by {{by}}.",
      toasts: { saved: "Secret stored.", rotated: "Secret rotated.", deleted: "Secret deleted." },
      empty: "No secrets yet.",
      emptyHint: "Create one and reference it as secret://key from connectors and providers.",
    },
  },
  identity: {
    title: "Identity & Access",
    subtitle: "Who can do what: the staff and applicant directory, roles with their permission matrix, identity providers, and machine clients that call the platform API.",
    tabs: { users: "Users", roles: "Roles & permissions", apiClients: "API clients", providers: "Identity providers" },
    areas: { platform: "Platform Console", cases: "Cases", ai: "AI", databank: "Data Bank", api: "Machine API" },
    help: {
      roles: "Roles are data: built-in roles mirror the ministry's hierarchy (Officer → Minister) and can be granted extra permissions; new roles (a Janahi-style committee secretary, for example) are created here and immediately usable in workflows and automations.",
      users: "Staff sign in with their ministry identity; applicants with UAE Pass. Console roles assigned here take effect on the next request – no re-login needed.",
      apiClients: "API clients are for systems, not people: they exchange a client id and secret for a short-lived token whose scopes are the api.* permissions, optionally restricted to services and to an IP allow-list.",
      providers: "Identity providers let staff sign in with the ministry directory. Directory groups map to roles and workspaces at every sign-in; Recent sign-ins shows what each sign-in matched and why a refusal happened.",
    },
    providers: {
      hint: "Staff sign in with the ministry directory (Microsoft Entra ID or another OpenID Connect provider). Every sign-in is checked against the provider's published signing keys; the account's groups then decide its platform roles and workspace memberships, so joiners, movers and leavers follow the directory.",
      newProvider: "New provider",
      editProvider: "Edit provider",
      dialogHint: "Register the platform as an application with the provider (redirect address: each staff surface's /login page), then enter the issuer, client id and secret, and map the directory groups.",
      empty: "No identity provider yet",
      emptyHint: "Add Microsoft Entra ID or any OpenID Connect issuer so staff sign in with their ministry account.",
      simulated: "Simulated",
      simulatedHint: "Served by the simulators: sign-in pages show the simulated directory's accounts instead of the provider's own page. Works in demo mode only.",
      kinds: { EntraId: "Microsoft Entra ID", Oidc: "OpenID Connect", IdentityServer: "Identity Server" },
      linkedUsers_one: "{{count}} linked user",
      linkedUsers_other: "{{count}} linked users",
      lastSignIn: "Last sign-in {{when}}",
      noSignIns: "No sign-ins yet",
      tested: "Tested {{when}}",
      neverTested: "Not tested yet",
      test: "Test connection",
      testOk: "Connection OK",
      testFailed: "Connection failed",
      signIns: "Recent sign-ins",
      signInsHint: "The latest sign-ins through {{name}}: the groups that matched and what they granted. Refusals say why.",
      noRecentSignIns: "Nobody has signed in through this provider yet.",
      outcome: { signedIn: "Signed in", refused: "Refused", failed: "Failed" },
      newAccount: "New account",
      linkedByEmail: "Linked by e-mail",
      matched: "Matched groups",
      granted: "Granted",
      mappingsSummary: "{{roles}} role mappings · {{workspaces}} workspace mappings",
      policy: { jit: "Creates accounts on first sign-in", noJit: "Pre-provisioned accounts only", sync: "Directory is authoritative", link: "Links by e-mail" },
      domains: "Domains: {{domains}}",
      deleteTitle: "Delete provider",
      deleteConfirm: "Delete “{{name}}”? Staff can no longer sign in with it. Their accounts and links are kept, so re-creating the provider under the same key restores sign-in.",
      sections: { general: "General", connection: "Connection", claims: "Claims", roles: "Role mappings", workspaces: "Workspace mappings", policy: "Sign-in policy" },
      fields: {
        kind: "Provider type",
        issuer: "Issuer",
        issuerHint: "Exactly as the provider publishes it; the platform reads /.well-known/openid-configuration from it. Entra ID: https://login.microsoftonline.com/<tenant id>/v2.0",
        clientId: "Client (application) id",
        clientSecret: "Client secret",
        clientSecretHint: "A secret from Settings › Secrets. None for a public client (PKCE only).",
        noSecret: "None (public client)",
        scopes: "Scopes",
        scopesHint: "Space-separated; must include openid.",
        pkce: "PKCE",
        pkceHint: "Proof key for the code exchange. Keep it on.",
        advanced: "Endpoint overrides",
        advancedHint: "Only needed when the provider's discovery document is missing or wrong.",
        authorizationEndpoint: "Authorization endpoint",
        tokenEndpoint: "Token endpoint",
        userInfoEndpoint: "Userinfo endpoint",
        jwksUri: "JWKS address",
        endSessionEndpoint: "Sign-out endpoint",
        extraParams: "Extra authorize parameters",
        extraParamsHint: "For example prompt = select_account or domain_hint = moet.gov.ae.",
        subjectClaim: "Subject claim",
        subjectClaimHint: "Identifies the person across sign-ins. Entra ID: oid (the object id).",
        emailClaim: "E-mail claim",
        nameClaim: "Name claim",
        nameArClaim: "Arabic name claim",
        groupsClaim: "Groups claim",
        groupsClaimHint: "Where group or app-role values arrive: groups, roles…",
        claim: "Claim",
        value: "Value",
        label: "Label",
        role: "Role",
        workspace: "Workspace",
        owner: "Owner",
        claimPlaceholder: "groups claim",
        valuePlaceholder: "Group object id or role value",
        labelPlaceholder: "e.g. SG-LegalAffairs-Counsel",
        choose: "Choose…",
        addRoleMapping: "Add role mapping",
        addWorkspaceMapping: "Add workspace mapping",
        rolesHint: "Anyone whose claim carries the value holds the role. Built-in roles land on the staff record, custom roles as assignments marked as coming from the directory.",
        workspacesHint: "Anyone whose claim carries the value becomes a member (or owner) of the workspace, on top of the roles the workspace admits.",
        defaultRoles: "Roles for everyone",
        defaultRolesHint: "Granted to every user of this provider, whatever their groups.",
        jit: "Create accounts on first sign-in",
        jitHint: "Off: only people who already have a platform account may sign in.",
        linkByEmail: "Link existing accounts by e-mail",
        linkByEmailHint: "A first sign-in attaches to the staff account with the same e-mail address.",
        sync: "Directory is authoritative",
        syncHint: "At each sign-in, roles and memberships granted through this provider are withdrawn once the groups stop carrying them. What the console grants is never touched.",
        allowedDomains: "Allowed e-mail domains",
        allowedDomainsHint: "Comma-separated, e.g. moet.gov.ae. Empty: anyone the issuer vouches for.",
        appEconic: "Signs in to ECONIC",
        appNcp: "Signs in to NCP (back office, admin console, portal)",
        appsHint: "ECONIC and NCP are registered separately in the Ministry's directory. Neither ticked: the provider signs in to both.",
        mfaWithin: "Directory MFA within (minutes)",
        mfaWithinHint: "The directory's multi-factor authentication stands in for the platform's: every sign-in must show one completed this recently, else it is asked for again. Empty: only where the host requires MFA.",
        profileSync: "Profile from the directory",
        profileSyncHint: "At every sign-in: name, job title, department, mobile, manager and photo from Microsoft Graph. A field the directory leaves empty keeps the platform's value.",
        arabicName: "Arabic name attribute",
        arabicNameHint: "The on-premises extension attribute holding the Arabic name, extensionAttribute1 to extensionAttribute15. Empty: the Arabic name stays as the platform holds it.",
        enabledHint: "Shown on the staff sign-in pages.",
      },
      toasts: { created: "Provider created.", saved: "Provider saved.", deleted: "Provider deleted.", enabled: "Provider enabled.", disabled: "Provider disabled." },
    },
    roles: {
      hint: "Tick a cell to grant a permission to a role. Gold cells come from an area wildcard (e.g. platform.*) and are edited on the role itself. The administrator role is always a superuser.",
      matrix: "Permission matrix",
      matrixHint: "Roles across, permissions down, grouped by area.",
      permission: "Permission",
      allAreas: "All areas",
      showDisabled: "Show disabled roles",
      newRole: "New role",
      editRole: "Edit role",
      builtIn: "Built-in",
      builtInHint: "Mirrors a staff role from the ministry hierarchy. Permissions can be extended; the key and enabled state are fixed.",
      builtInEditHint: "Built-in roles keep their key and stay enabled; their permissions can be extended or trimmed.",
      dialogHint: "Give the role a name and pick its permissions. It can be assigned to people, referenced in workflow tasks and used to target feature flags right away.",
      disabled: "Disabled",
      members: "{{count}} members",
      permissionCount: "{{count}} permissions",
      all: "all",
      keyHint: "Stable identifier used in workflows and packages, e.g. committee-secretary.",
      rank: "Rank",
      rankHint: "Ordering in pickers; lower comes first.",
      color: "Colour",
      assignable: "Assignable",
      assignableHint: "Can be granted to users from the directory.",
      department: "Department", departmentHint: "A department role is offered in that department's workflows and services.", allDepartments: "Shared by all departments", departmentBadge: "Department: {{department}}",
      enabled: "Enabled",
      enabledHint: "Disabled roles grant nothing but keep their assignments.",
      permissions: "Permissions",
      clear: "Clear",
      superuser: "This role holds every permission (*). It cannot be narrowed here.",
      dangerous: "High-impact permission",
      dangerousSelected: "{{count}} high-impact permissions selected – they publish, approve or change security settings.",
      wildcardHint: "Granted through a wildcard – edit the role to change it.",
      viaWildcard: "Via wildcard",
      deleteTitle: "Delete role",
      deleteConfirm: "Delete “{{name}}”? Its assignments are removed; workflows that reference it keep the key.",
      reset: "Reset to defaults",
      resetHint: "Restore this built-in role's permissions to the platform defaults.",
      toasts: { created: "Role created.", saved: "Role saved.", deleted: "Role deleted.", reset: "Permissions restored to defaults." },
      builtInNames: { Admin: "Administrator", Minister: "Minister", Undersecretary: "Undersecretary", AssistantUndersecretary: "Assistant Undersecretary", Director: "Director", HeadOfComplaints: "Head of Complaints", EconomicResearcher: "Economic Researcher", LegalResearcher: "Legal Researcher", EconomicExpert: "Economic Expert", LegalExpert: "Legal Expert", JudicialOfficer: "Judicial Officer", CommitteeMember: "Committee Member", Applicant: "Applicant", Representative: "Representative", Auditor: "Auditor" },
    },
    users: {
      account: {
        title: "Sign-in account",
        hint: "Password and authenticator account. A reset issues a one-time password that is handed over in person; it is never e-mailed.",
        status: { active: "Active", first_sign_in: "Awaiting first sign-in", locked: "Locked", deactivated: "Deactivated", no_credential: "No password yet", directory: "Ministry account" },
        factor: "Authenticator",
        factorOn: "Set up",
        factorOff: "Not set up",
        sessions: "Active sessions: {{count}}",
        issue: "Issue a password",
        resetPassword: "Reset password",
        resetMfa: "Reset authenticator",
        unlock: "Unlock",
        signOut: "Sign out everywhere",
        tempTitle: "Temporary password (shown once)",
        tempHint: "Give it to the person directly. It is not e-mailed and cannot be shown again.",
        tempExpires: "Expires {{when}}",
        copy: "Copy",
        copied: "Copied",
        secondAdmin: "A second administrator must reset the authenticator.",
        selfChange: "Your own account is changed by another administrator.",
        unavailable: "Sign-in accounts are not available on this server yet.",
        done: "Done",
      },
      search: "Search by name, e-mail or title…",
      kinds: { Staff: "Staff", External: "Applicants", System: "System" },
      anyRole: "Any role",
      includeInactive: "Include inactive",
      columns: { user: "User", title: "Title / organisation", roles: "Roles", lastLogin: "Last sign-in", status: "Status" },
      active: "Active",
      inactive: "Inactive",
      demo: "Demo persona",
      viaProvider: "Signs in with {{name}}",
      fromDirectory: "From the directory",
      never: "Never signed in",
      lastLogin: "Last sign-in {{when}}",
      empty: "No users match.",
      builtInRoles: "Ministry roles",
      builtInRolesHint: "From the staff record (identity provider or seed). Changed by HR, not here.",
      assignedRoles: "Console roles",
      noAssigned: "No additional roles assigned.",
      assign: "Assign role",
      unassign: "Remove role",
      role: "Role",
      validUntil: "Valid until",
      validUntilHint: "Optional – for acting appointments and committee terms.",
      activate: "Activate",
      deactivate: "Deactivate",
      activateHint: "Restores sign-in and task assignment.",
      deactivateHint: "Blocks sign-in immediately; open tasks stay assigned until reassigned.",
      deactivateConfirm: "Deactivate {{name}}? They will be signed out and cannot sign in until reactivated.",
      toasts: { assigned: "Role assigned.", unassigned: "Role removed.", activated: "User activated.", deactivated: "User deactivated." },
    },
    apiClients: {
      hint: "Machine clients (OutSystems Lightspeed portal, DED integration, BI) use OAuth 2.0 client credentials. Scopes are the api.* permissions; a token never grants more than its client. Disabling a client rejects its tokens at once.",
      newClient: "New API client",
      editClient: "Edit API client",
      create: "Create client",
      dialogHint: "Pick the scopes the system needs, restrict to services where possible and pin the caller's network. The secret is shown once after creation.",
      empty: "No API clients yet.",
      emptyHint: "Create one for the external portal or a partner system, then subscribe it to webhooks.",
      clientId: "Client id",
      clientIdHint: "Lowercase identifier the system sends as client_id.",
      clientSecret: "Client secret",
      secretHint: "secret {{hint}}",
      scopes: "Scopes",
      scopesHint: "Permission keys the token may carry.",
      needScope: "Grant at least one scope.",
      services: "Services",
      servicesHint: "Empty = every service. Restricting scopes case data to these services.",
      allServices: "All services",
      restrictedTo: "Restricted to {{count}} services",
      ipAllowList: "IP allow-list",
      ipAllowListHint: "IP addresses or CIDR ranges, one per line. Empty = any address. Checked at token issue and on every request.",
      anyIp: "Any address",
      tokenLifetime: "Token lifetime",
      tokenLifetimeHint: "5–1440 minutes.",
      minutes: "{{count}} min",
      lastUsed: "Last token",
      neverUsed: "Never",
      tokensIssued: "Tokens issued",
      owner: "Owner",
      contactEmail: "Contact e-mail",
      enabled: "Enabled",
      disabled: "Disabled",
      webhooks: "{{count}} webhooks",
      rotatedAt: "Secret rotated {{when}}",
      rotate: "Rotate secret",
      rotateTitle: "Rotate client secret",
      rotateConfirm: "Issue a new secret for “{{name}}”? The current secret stops working immediately; existing tokens remain valid until they expire.",
      deleteTitle: "Delete API client",
      deleteConfirm: "Delete “{{name}}”? Its tokens are rejected immediately and its webhooks are left without an owner.",
      credentialsTitle: "Client credentials",
      credentialsHint: "Hand these to the integrating team through a secure channel.",
      credentialsOnce: "The secret is shown only now. Store it in the partner system's secret store; rotate it here if it leaks.",
      stored: "I have stored the secret",
      howTo: "Using the machine API",
      howToHint: "Exchange the credentials for a bearer token (client_credentials), then call /api/v1 with it. E-services submit requests to the playbooks marked “e-services” and follow them by their own reference.",
      procedures: "Procedures",
      playbooks: "Playbooks",
      eServices: "e-services",
      unpublished: "No longer published:",
      removeService: "Remove {{key}}",
      toasts: { created: "API client created.", saved: "API client saved.", rotated: "Secret rotated.", enabled: "Client enabled.", disabled: "Client disabled.", deleted: "API client deleted." },
    },
  },
  webhooks: {
    title: "Outbound webhooks",
    hint: "Every platform event (case submitted, state changed, clock at risk, proposal approved…) is offered to matching subscriptions and posted with an HMAC signature. Failures retry with back-off; the log shows every attempt.",
    subscriptions: "Subscriptions",
    enabledCount: "{{count}} enabled",
    pending: "Pending",
    pendingHint: "Queued or waiting for a retry.",
    delivered24h: "Delivered",
    failed24h: "Failed",
    last24h: "Last 24 hours",
    empty: "No webhooks yet.",
    emptyHint: "Subscribe the external portal or a partner system to case events.",
    newWebhook: "New webhook",
    editWebhook: "Edit webhook",
    dialogHint: "Choose the events and services to forward, the endpoint, and the secret used to sign each delivery.",
    url: "Endpoint URL",
    urlHint: "https required (plain http only for local targets).",
    signingSecret: "Signing secret",
    signingSecretHint: "The receiver verifies X-NCP-Signature: t=<unix>,v1=HMAC-SHA256(secret, \"<t>.<body>\").",
    signed: "Signed",
    unsigned: "Unsigned",
    secretMissing: "signing secret not defined",
    apiClient: "Owning API client",
    apiClientHint: "Optional – lets that client list its subscriptions through the API.",
    headers: "Extra headers",
    headersHint: "One per line as Name: value. Values may be secret:// references.",
    maxAttemptsLabel: "Max attempts",
    maxAttempts: "{{count}} attempts",
    events: "Events",
    eventsHint: "Exact types or patterns like case.*.",
    allEvents: "Every event",
    customEvent: "custom.event.type",
    services: "Services",
    allServices: "All services",
    restrictedTo: "{{count}} services",
    ownedBy: "Owner: {{name}}",
    failing: "{{count}} failures in a row",
    lastDelivered: "Last delivered {{when}}",
    neverDelivered: "Never delivered",
    stats: "{{pending}} pending · {{ok}} ok · {{failed}} failed (24h)",
    viewDeliveries: "Deliveries",
    test: "Send test",
    deliveryLog: "Delivery log",
    deliveryLogHint: "Latest 200 deliveries. Click a row for the payload and the last error.",
    allWebhooks: "All webhooks",
    anyStatus: "Any status",
    noDeliveries: "No deliveries yet.",
    columns: { when: "When", webhook: "Webhook", event: "Event", status: "Status", attempts: "Attempts", next: "Next / result" },
    status: { Pending: "Pending", Delivered: "Delivered", Failed: "Failed", Exhausted: "Exhausted" },
    delivery: "Delivery",
    eventId: "Event id",
    deliveryId: "Delivery id",
    deliveredAt: "Delivered at",
    attemptsCount: "{{count}} attempts",
    case: "Case",
    lastError: "Last error",
    payload: "Payload",
    redeliver: "Redeliver",
    deleteTitle: "Delete webhook",
    deleteConfirm: "Delete “{{name}}” and its delivery history?",
    toasts: { created: "Webhook created.", saved: "Webhook saved.", deleted: "Webhook deleted.", enabled: "Webhook enabled.", disabled: "Webhook disabled.", testOk: "Test delivered in {{ms}} ms.", testFailed: "Test delivery failed.", redelivered: "Queued for redelivery." },
  },
  integrationsTabs: { monitor: "Monitor", webhooks: "Webhooks", connectors: "Connectors", connectorsSoon: "Connector Builder arrives with the next console release." },
  connectors: {
    title: "Connector Builder",
    subtitle: "REST and SOAP connections to statistics, open-data and partner systems, built without code: authentication by secret reference, operations with templated requests and response mapping, released through change sets. Agents, assistants and automations are granted operations by name.",
    new: "New connector",
    search: "Search connectors…",
    count: "Connectors: {{count}}",
    emptyTitle: "No connectors yet", emptyBody: "Build a connector to a statistics, open-data or partner API; agents, assistants and automations can then use its operations.",
    operations: "Operations",
    unused: "Not used yet",
    usage: { agent: "Agents: {{count}}", assistant: "Assistants: {{count}}", automation: "Automations: {{count}}" },
    usageKind: { agent: "Agent", assistant: "Assistant", automation: "Automation" },
    kind: { Rest: "REST", Soap: "SOAP", Smtp: "SMTP", Sms: "SMS", InboundWebhook: "Inbound webhook", Mcp: "MCP", Database: "Database" },
    tabs: { general: "General", auth: "Authentication", operations: "Operations", resilience: "Resilience", environments: "Environments", test: "Test", calls: "Calls", code: "Code", versions: "Versions" },
    create: {
      intro: "Name the connector and where it points. Operations, authentication and resilience are set in the builder; nothing is live until the version is released.",
      namePlaceholder: "e.g. FCSC statistics",
      keyHint: "Lowercase letters, digits and '-'. Grants and automations refer to it: fcsc-statistics.key-indicators.",
      keyInvalid: "2–48 lowercase letters, digits or '-', starting with a letter.",
      created: "Connector created as a draft.",
      createOpen: "Create and open",
    },
    fields: {
      name: "Name", value: "Value", label: "Label", labelAr: "Label (Arabic)", default: "Default", example: "Example", required: "Required", addRow: "Add",
      descriptionPlaceholder: "What the value means – the model reads this when it fills the value in.",
      kind: "Kind", owner: "Owner", ownerHint: "The organisation that runs the system (FCSC, Bayanat…).",
      baseUrl: "Base URL", baseUrlHint: "Absolute http(s) address. Start with {{simulators}} to use the platform's simulators ({{url}}).",
      inboundPath: "Inbound path", inboundPathHint: "Outside systems call /api/inbound/{path}.",
      inboundSecret: "Signing secret", inboundSecretHint: "Optional; callers sign the body with this secret (HMAC).",
    },
    general: {
      connection: "Connection", connectionHint: "Where the connector points. Environments can override the address and headers.",
      identityHint: "How the connector appears in pickers, the monitor and the call log.",
      descriptionHint: "What the system is and what data it offers – agents and assistants see it next to each operation.",
      enabledHint: "A disabled connector stays in the catalogue but no agent, assistant or automation can call it.",
      usage: "Who uses it", usageHint: "Published agents, assistants and automations granted this connector's operations.",
    },
    auth: {
      title: "Authentication", hint: "How every call proves who it is. Credentials are secret:// references resolved at call time – values never appear in a definition, an export or the log.",
      kindLabel: "Method",
      kind: { None: "None", ApiKey: "API key", Basic: "Basic (username and password)", Bearer: "Bearer token", OAuth2ClientCredentials: "OAuth 2.0 client credentials", MutualTls: "Mutual TLS (client certificate)", Hmac: "HMAC request signature" },
      explain: {
        None: "Calls carry no credentials – for public APIs such as the IMF DataMapper or the World Bank.",
        ApiKey: "A static key sent in a header (or the query string) on every call.",
        Basic: "A username and password sent as an HTTP Basic header.",
        Bearer: "A long-lived token sent as Authorization: Bearer.",
        OAuth2ClientCredentials: "The runtime requests a token from the token URL with the client id and secret, caches it until it expires and sends it as a bearer token.",
        MutualTls: "The runtime presents a client certificate (PEM) during the TLS handshake.",
        Hmac: "Every request body is signed with a shared secret (HMAC-SHA256) in a header.",
      },
      field: { name: "Header or parameter name", value: "Key", in: "Sent in", username: "Username", password: "Password", token: "Token", tokenUrl: "Token URL", clientId: "Client id", clientSecret: "Client secret", scope: "Scope", audience: "Audience", certificate: "Certificate (PEM)", privateKey: "Private key (PEM)", secret: "Shared secret", header: "Signature header" },
      fieldHint: { name: "e.g. X-API-Key", scope: "Optional; space-separated scopes.", audience: "Optional; some identity providers need it.", header: "Defaults to X-Signature." },
      inHeader: "A header", inQuery: "The query string",
      hasValue: "set", noValue: "no value yet", otherSecret: "Another secret reference…",
      mustBeReference: "Must be a secret:// reference, never the value itself.",
      secretsNote: "Secrets hold the values, encrypted.", manageSecrets: "Manage secrets",
      headers: "Default headers", headersHint: "Sent with every call (operations and environments can add or override). Values may use secret:// references.",
    },
    openapi: {
      open: "Import from OpenAPI", title: "Import operations from an OpenAPI description",
      intro: "Upload or paste the system's OpenAPI (or Swagger 2) description in JSON. Each call becomes an operation with its parameters, body template and response mapping; GET calls are read-only.",
      file: "File", document: "Description (JSON)", tag: "Only operations tagged", tagHint: "Optional. Leave empty to import every operation.",
      read: "Read description", found: "{{found}} operations found, {{fresh}} new", more: "and {{n}} more", add: "Add {{n}} operations",
    },
    ops: {
      title: "Operations", hint: "Each operation is one call agents, assistants and automations can be granted.",
      untitled: "Untitled operation", newName: "Operation {{n}}", copy: "copy", add: "Add operation", duplicate: "Duplicate", none: "No operations yet.",
      emptyTitle: "No operations", emptyBody: "Add the first call this connector offers – for example the latest key indicators.",
      writes: "Changes data in the external system", health: "health",
      call: "Call", callHint: "The request line: method, path under the base URL, and whether the call only reads.",
      keyHint: "2–48 lowercase letters, digits or '-'.",
      descriptionHint: "Tell the model when to use it and what it returns; this is the tool description agents and assistants see.",
      method: "Method", path: "Path", pathHint: "Starts with '/'; {{placeholders}} are filled from the values below.",
      timeout: "Timeout (ms)", timeoutHint: "Optional; overrides the connector's.",
      readOnly: "Only reads data", readOnlyHint: "Agents and automations may call read operations only; assistants ask the user to confirm anything else.",
      idempotent: "Safe to repeat", idempotentHint: "A failed attempt may be retried without side effects (always true for GET).",
      parameters: "Values it takes", parametersHint: "Each {{placeholder}} used in the path, query, headers or body. Defaults may use built-in dates.",
      noParameters: "This operation takes no values.", addParameter: "Add value",
      missingParams: "Used but not declared:", builtins: "Built-ins:",
      request: "Request", requestHint: "Query values and headers for this call; values may use {{placeholders}}.",
      query: "Query string", headers: "Headers", body: "Body template", bodyHint: "JSON (or XML for SOAP) with {{placeholders}}.",
      response: "Response", responseHint: "Pick named fields out of the answer and turn an array into a table (JSONPath: $.data.items, $.items[0].name).",
      mapping: "Fields", mappingHint: "Name → JSONPath of a single value (the as-of date, a total…).",
      rowsPath: "Rows", rowsPathHint: "JSONPath of the array that becomes the table; leave empty when there is no table.",
      columns: "Columns", noColumns: "No columns: the table takes every field of the first row.", addColumn: "Add column", addStar: "All other fields (*)",
      starHint: "A * column expands into every field of the row not already listed – for year-keyed answers such as the IMF's.",
    },
    resilience: {
      calls: "Timeouts and retries", callsHint: "How long a call may take and how often a failed read is repeated (with exponential back-off).",
      timeout: "Timeout (ms)", retries: "Retries", backoff: "Back-off (ms)",
      retryNote: "Only reads and operations marked safe to repeat are retried; 4xx answers other than 408 and 429 are never retried.",
      breaker: "Circuit breaker", breakerHint: "After repeated failures the connector stops calling the system for a while, so a failing partner does not slow every agent and page down.",
      threshold: "Open after (failures)", openFor: "Stay open for (s)",
      health: "Health check", healthHint: "A read the monitor's probe calls to check the system is up.", noHealth: "None – the probe uses the base URL",
      logCalls: "Log requests and responses", logCallsHint: "Keeps a summary of each call (credentials masked) for the call log and audits.",
    },
    env: {
      title: "Environments", hint: "Per-environment address and headers; the platform currently runs as '{{env}}'. Environments without overrides use the connector's settings.",
      active: "active", overridden: "overridden", inherits: "inherits the connector's settings", headers: "Extra headers",
    },
    test: {
      title: "Test an operation", hint: "Calls version {{version}} through the runtime – real authentication, retries, breaker and call log. Drafts can be tested before release.",
      operation: "Operation", environment: "Environment", environmentHint: "Which environment's address and headers to use (the platform runs as '{{env}}').", platformEnvironment: "Platform default ({{env}})",
      run: "Run", saveAndRun: "Save and run", saveFirst: "Unsaved changes are saved before the call.",
      writeWarning: "This operation changes data in the external system. Test against a sandbox environment.",
      missing: "Fill in: {{names}}", empty: "Run an operation to see the request, the mapped fields and the table.",
      result: "HTTP {{status}} in {{ms}} ms", ok: "Succeeded", failed: "Failed", attempts: "Attempts: {{count}}",
      table: "Table ({{count}})", fields: "Fields ({{count}})", request: "Request", raw: "Raw response",
      noRows: "No table: set the rows path under Operations → Response.", noFields: "No fields mapped.",
      masked: "Credentials are masked in what the platform shows and logs.",
      noOperations: "Nothing to test yet", noOperationsBody: "Add an operation under Operations first.",
    },
    calls: { title: "Call log", hint: "Every call through this connector – tests, agents, assistants and automations – newest first.", live: "Live", none: "No calls yet.", purpose: "Purpose · version · environment" },
    grants: {
      none: "No published connector yet.", build: "Build one in the Connector Builder.", open: "Open connector", all: "All operations",
      everything: "Granted every connector ('*').", agentsRead: "Agents only read: operations that change data are for assistants, with the user's confirmation.",
      allReadsOnly: "Agents use the read operations only.", unknown: "Not in the catalogue (unpublished or removed):",
    },
    builder: {
      notFound: "Connector not found", notFoundBody: "No connector with the key '{{key}}'.", helpTitle: "Connector Builder",
      summary: "{{operations}} operation(s) · {{auth}} · used by {{users}}",
      stageTitle: "Connector: {{name}}", stageBody: "The version goes into a change set for review; once released, grants and automations call it.",
      publishedNotice: "This version is live. Create a new version to change it; the live one keeps serving until the new one is released.",
    },
    help: {
      what: "A connector is a versioned definition of how to call an outside system: its address, authentication, operations and resilience settings.",
      secrets: "Credentials are secret:// references to Settings → Secrets, resolved at call time; exports and the log never contain them.",
      grants: "Agents and assistants are granted operations by name (fcsc-statistics.key-indicators or fcsc-statistics.*). Agents only read; assistants ask the user before any call that changes data. Automations use the 'Call a connector' action.",
    },
    lint: {
      name: "Give the connector a name.",
      baseUrl: "The base URL must be an absolute http(s) address, or start with {{simulators}}.",
      timeout: "The timeout is between 500 ms and 120 s.", retries: "Retries are between 0 and 5.", backoff: "The back-off is between 0 and 30 s.",
      breaker: "The breaker opens after 1 to 50 failures.", breakerOpen: "The breaker stays open between 5 seconds and an hour.",
      authField: "Authentication needs '{{field}}'.", authSecret: "'{{field}}' must be a secret:// reference.",
      health: "The health check operation is not one of the operations.", envUrl: "The base URL for '{{env}}' must be an absolute http(s) address.",
      inboundPath: "An inbound webhook needs a path.",
      noOperations: "No operations yet – a connector needs at least one before it can be released.",
      noColumns: "An operation has a rows path but no columns: its table takes every field of the first row.",
      writes: "Some operations change data: agents and automations cannot call them, and assistants ask the user first.",
      opKey: "{{op}}: the key must be 2–48 lowercase letters, digits or '-'.", opDuplicate: "{{op}} is defined twice.", opMethod: "{{op}}: unknown method.",
      opPath: "{{op}}: the path starts with '/'.", opTimeout: "{{op}}: the timeout is between 500 ms and 120 s.",
      paramName: "{{op}}: every value needs a name.", paramDuplicate: "{{op}}: a value is declared twice.",
      undeclared: "{{op}}: {{tokens}} is used but not declared as a value.",
      mapping: "{{op}}: the field '{{field}}' needs a JSONPath such as $.data.value.", rowsPath: "{{op}}: the rows path must be a JSONPath such as $.items.",
      column: "{{op}}: column '{{column}}' needs a JSONPath inside the row.", body: "{{op}}: the body template is not valid JSON.",
    },
  },
  forms: {
    title: "Forms",
    subtitle: "Schema-driven intake, RFI, objection and internal forms. Design steps and fields, add conditional logic and cross-field rules, preview in both languages, then stage a version for release.",
    newForm: "New form",
    searchPlaceholder: "Search forms…",
    emptyTitle: "No forms yet",
    emptyBody: "Start from a template or import a form exported from another deployment. Forms are versioned; only released versions reach applicants.",
    noMatch: "No forms match the current filters.",
    help: {
      what: "A form is a JSON Schema (the data) plus a UI schema (steps, sections, field order, conditions) plus JsonLogic rules. The same document renders in the portal wizard, the back-office read-only view and this builder.",
      versions: "Saving edits the current draft. Staging adds the draft to a release; when the release is published the version becomes read-only and a new version can be started from it.",
      bindings: "Bindings map form paths to data-contract keys so compiled procedure logic (thresholds, fees) can read submissions produced by a dynamic form.",
    },
    stats: { total: "Forms", totalHint: "Distinct form keys", published: "Published", publishedHint: "Have a live version", drafts: "Drafts", draftsHint: "Being edited or in review", fields: "Fields", fieldsHint: "Across displayed versions" },
    filters: { published: "Published", hasDraft: "Has draft", inReview: "In review" },
    columns: { published: "Published", draft: "Draft", fields: "Fields", usedBy: "Used by" },
    purpose: { Intake: "Intake", Rfi: "Request for information", Objection: "Objection", Internal: "Internal" },
    status: { Draft: "Draft", InReview: "In review", Published: "Published", Retired: "Retired" },
    tabs: { design: "Design", rules: "Rules", preview: "Preview", code: "Code", settings: "Settings", versions: "Versions" },
    create: {
      intro: "Pick a starting point, give the form a name and a key. You can change everything except the key later.",
      fromTemplate: "From a template", fromTemplateHint: "Blank, enquiry or transaction notification.",
      import: "Import", importHint: "A .form.json exported from the Code tab.",
      importPrompt: "Choose a .form.json file exported from another deployment.", importReady: "Loaded – {{fields}} top-level fields.", chooseFile: "Choose file", fileLoaded: "{{name}} loaded.",
      namePlaceholder: "e.g. Merger notification – Form CD-F-01",
      keyInvalid: "Lower-case letters, digits and hyphens; 2–64 characters; starts with a letter.", keyTaken: "A form with this key already exists.",
      createOpen: "Create and open", created: "Form created.",
    },
    builder: {
      helpTitle: "Form Builder",
      help: {
        design: "Drag widgets from the palette into a section, or click one to append it to the selected section. Click a field to edit its label, validation, widget options and conditions in the properties pane.",
        rules: "Rules are JsonLogic conditions that, when true, block submission (error) or warn. They can compare fields with each other and use table helpers (count, sum, any).",
        publish: "Save keeps the draft. Stage for release adds it to a change set for four-eyes approval. Published versions are immutable – start a new version to edit.",
        shortcuts: "{{mod}}S save · {{mod}}Z undo · ⇧{{mod}}Z redo · Delete removes the selected field.",
      },
      summary: "{{fields}} fields · {{steps}} steps · {{rules}} rules",
      warnings: "{{count}} warnings",
      undo: "Undo", redo: "Redo",
      save: "Save draft", stage: "Stage for release", discard: "Discard draft", newVersion: "New version", withdraw: "Withdraw from review",
      staged: "Draft added to a release.", stageTitle: "Form: {{name}}",
      stageDialogTitle: "Stage for release", stageBody: "The draft is saved and added to a change set. A second administrator approves the release before it goes live.",
      discardTitle: "Discard draft", discardBody: "The draft version is deleted. Published versions are not affected.", discarded: "Draft discarded.",
      newVersionTitle: "Start a new version", newVersionBody: "Creates draft v{{version}} as a copy of this version. Running cases keep the version they started on.", versionCreated: "Version {{version}} created.",
      withdrawn: "Withdrawn from review – the draft is editable again.",
      fixErrors: "Fix {{count}} lint errors before saving.",
      inReviewNotice: "This version is in review as part of a release. Withdraw it to edit, or wait for the release decision.",
      publishedNotice: "This version is published and read-only. Start a new version to make changes.",
      notFound: "Form not found", notFoundBody: "No form with key “{{key}}” exists.",
    },
    design: {
      paletteHint: "Drag into a section or click to add", searchWidgets: "Search widgets…", contentBlock: "Guidance text",
      addStep: "Add step", addReviewStep: "Add review step", reviewStep: "Review", reviewStepHint: "Summary of all answers and the declaration, shown before submit.",
      removeStep: "Remove step", addSection: "Add section", emptySection: "Drop fields here", noSteps: "No steps yet – add one to start placing fields.",
      noSectionForAdd: "Add a section first.", columns: "{{count}} columns", conditional: "Conditional", conditionallyRequired: "Conditionally required",
      moveUp: "Move up", moveDown: "Move down", duplicate: "Duplicate", place: "Place", missingSchema: "Missing in schema",
      unplaced: "{{count}} schema fields are not placed in any step", orphans: "{{count}} placed fields do not exist in the schema",
      confirmRemoveField: "Remove this field from the step? Choose OK to also delete it from the schema, Cancel to keep the data definition.",
      confirmRemoveSection: "Remove this section and its fields from the step?", confirmRemoveStep: "Remove this step and all its sections?",
    },
    widgetCategories: { basic: "Basic", choice: "Choice", number: "Numbers", date: "Dates", composite: "Composite", documents: "Documents", layout: "Layout" },
    props: {
      nothingSelected: "Select a step, section or field to edit its properties.",
      step: "Step", section: "Section", field: "Field",
      groups: { identity: "Identity", validation: "Validation", widget: "Widget", behaviour: "Behaviour", data: "Data" },
      stepTitle: "Step title", sectionTitle: "Section title", title: "Title", english: "English", arabic: "Arabic",
      key: "Key", keyHint: "Property name in the JSON Schema and submission. Renaming updates every reference.", keyErrors: { invalidKey: "Letters, digits and underscore; must start with a letter.", notFound: "Field not found.", duplicateKey: "Another field already has this key." },
      label: "Label", hint: "Hint", help: "Help text", placeholder: "Placeholder", unit: "Unit", icon: "Icon", iconHint: "Lucide icon name (optional).",
      widget: "Widget", format: "Format", default: "Default value", defaultHint: "Pre-filled when the form opens.", pattern: "Pattern (regex)", patternHint: "JavaScript regular expression without slashes.",
      minLength: "Min length", maxLength: "Max length", minimum: "Minimum", maximum: "Maximum", multipleOf: "Step", minItems: "Min rows", maxItems: "Max rows",
      options: "Options", addOption: "Add option", optionValue: "Value", deprecated: "Deprecated",
      visibleWhen: "Visible when", visibleWhenHint: "Hidden fields are not validated and are dropped from the submission.", requiredWhen: "Required when", requiredWhenHint: "In addition to the static Required flag.", disabledWhen: "Disabled when",
      addCondition: "Add condition", stepVisibleHint: "Skip the whole step unless the condition holds.",
      readOnly: "Read-only", sensitive: "Sensitive", sensitiveHint: "Masked in the back-office read-only view and in agent context.",
      span: "Width", spanAuto: "Auto", spanHint: "Columns the field spans in the section grid.", columns: "Columns", collapsible: "Collapsible", collapsedByDefault: "Collapsed by default",
      direction: "Text direction", dirAuto: "Auto", binding: "Data-contract binding", bindingHint: "Key under which compiled procedure logic reads this value.",
      declaration: "Declaration text", declarationHint: "Shown with the confirmation checkbox on the review step.", reviewHint: "The review step lists every answered field and the declaration.",
      nestedHint: "Nested object – edit its children by selecting them in the canvas.",
    },
    rules: {
      intro: "Cross-field rules run on the client and again on the server. A rule that evaluates to true is a violation.",
      add: "Add rule", emptyTitle: "No rules yet", emptyBody: "Add a rule to compare fields with each other, e.g. closing date after signing date or combined market share above a threshold.",
      when: "Fires when", condition: "Condition", conditionHint: "Build the condition that describes the violation. Use @ to reference another field.",
      target: "Highlight field", targetHint: "Where the message appears.", wholeForm: "Whole form", severity: "Severity", error: "Error – blocks submission", warning: "Warning – shown, does not block",
      messageEn: "Message (English)", messageAr: "Message (Arabic)", explanation: "Explanation", explanationHint: "Shown to authors and in the review step.",
      firesOnSample: "Fires on sample", passesOnSample: "Passes on sample", fires: "Fires", passes: "Passes",
      testBench: "Test bench", testBenchHint: "Evaluated against the saved sample data (Preview → Save as sample).", noSample: "No sample data yet. Fill the preview and choose “Save as sample” to test rules here.",
      operators: "Operators", operatorsHint: "Standard JsonLogic plus NCP helpers, available on both client and server.",
    },
    preview: {
      wizard: "Wizard", single: "Single page", readonly: "Read-only", mobile: "Mobile", desktop: "Desktop",
      clear: "Clear", loadSample: "Load sample", saveAsSample: "Save as sample", sampleSaved: "Sample data updated – save the draft to keep it.", submitted: "Submitted (preview only).",
      clientValidation: "Client validation", clientHint: "Ajv + rules, as the portal runs them.", serverValidation: "Server validation", serverHint: "The same document validated by the API – should agree with the client.",
      runServer: "Validate on server", saveFirst: "Save the draft first to validate on the server.", noIssues: "No issues.", data: "Submission data",
      parityOk: "Client and server agree.", parityDiff: "Client reports {{client}} errors, server {{server}} – review the differences.",
    },
    code: {
      parts: { schema: "JSON Schema", uiSchema: "UI schema", rules: "Rules", bindings: "Bindings", sampleData: "Sample data" },
      format: "Format", validJson: "Valid JSON – applied", fieldCount: "{{count}} fields",
      clientLint: "Lint", clientLintHint: "Structural checks run live as you type.", serverLint: "Server lint", serverLintHint: "Same checks on the API, including JSON Schema compilation.", runServerLint: "Run server lint",
      serverFieldCount: "Server counts {{count}} fields", countMismatch: "client counts {{client}}",
      importInvalid: "The file must contain schema and uiSchema.", imported: "Form imported into the draft.",
      xncpHint: "x-ncp carries bilingual titles, widget choice and hints inside the schema; it is ignored by standard JSON Schema validators.",
    },
    settings: {
      identity: "Identity", keyHint: "Immutable identifier used by services and packages.", purpose: "Purpose", purposeHint: "Where the form is offered: applicant intake, RFI reply, objection or internal use.",
      tags: "Tags", tagsHint: "Free-form labels for search and packaging.", addTag: "Add tag",
      bindings: "Data-contract bindings", bindingsHint: "Map form paths to the keys compiled procedure logic reads from the submission.", formPath: "Form path", contractKey: "Contract key", noBindings: "No bindings yet.", addBinding: "Add",
      usedBy: "Used by services", usedByHint: "Services whose intake form is this key.", notUsed: "Not referenced by any service yet.",
      whereRendered: "Where it renders", whereRenderedHint: "The portal wizard for applicants and the Application data panel in the back office share this definition.", openBackoffice: "Open back office",
    },
    versions: {
      title: "History", emptyTitle: "No versions", emptyBody: "Versions appear once the form is saved.", editing: "Open", publishedAt: "Published {{at}}", updatedAt: "Updated {{at}}", changeSet: "Release",
      compare: "Compare", onlyOne: "Only one version exists – nothing to compare yet.", summary: "Summary",
    },
  },
  common: {
    key: "Key",
    name: "Name",
    nameAr: "Name (Arabic)",
    description: "Description",
    descriptionAr: "Description (Arabic)",
    refresh: "Refresh",
    version: "Version",
    status: "Status",
    updated: "Updated",
    createdBy: "Created by",
    actions: "Actions",
    draft: "Draft",
    inReview: "In review",
    published: "Published",
    retired: "Retired",
    active: "Active",
    inactive: "Inactive",
    enabled: "Enabled",
    disabled: "Disabled",
    yes: "Yes",
    no: "No",
    none: "None",
    all: "All",
    search: "Search",
    filter: "Filter",
    create: "Create",
    save: "Save",
    saving: "Saving…",
    saved: "Saved.",
    cancel: "Cancel",
    delete: "Delete",
    duplicate: "Duplicate",
    export: "Export",
    import: "Import",
    test: "Test",
    preview: "Preview",
    publish: "Publish",
    close: "Close",
    back: "Back",
    next: "Next",
    done: "Done",
    edit: "Edit",
    open: "Open",
    copy: "Copy",
    copied: "Copied.",
    confirm: "Confirm",
    required: "Required",
    optional: "Optional",
    loading: "Loading…",
    empty: "Nothing here yet.",
    error: "Something went wrong.",
    errorDetail: "The request could not be completed. Please try again.",
    adminOnly: "Only platform administrators can change this.",
    permissionDenied: "You do not have permission to do this.",
    changeNote: "Change note",
    changeNoteHint: "Why this change is made – shown in the release history and the audit chain.",
    json: "JSON",
    visual: "Visual",
    code: "Code",
    diff: "Diff",
    history: "History",
    rollback: "Roll back",
    validate: "Validate",
    valid: "Valid",
    invalid: "Invalid",
    issues: "{{count}} issues",
    unsaved: "Unsaved",
    add: "Add",
    remove: "Remove",
    change: "Change",
    clear: "Clear",
    builtIn: "Built-in",
  },
  solutions: {
    title: "Solutions",
    subtitle: "Every surface the platform serves: web apps, embedded panels, channels and external portals. Where records open in each, who may see it, and whether it is up.",
    newSolution: "Register a solution",
    searchPlaceholder: "Search by name or key…",
    noMatch: "No solution matches these filters",
    emptyTitle: "No solutions registered yet",
    linkCount: "Links: {{count}}",
    usedBy: "Used by",
    everywhere: "Every workspace",
    threadsMonth: "Conversations this month",
    notFound: "Solution not found",
    notFoundBody: "There is no solution with the key “{{key}}”. It may have been deleted.",
    help: {
      what: "A solution is anywhere people work with the platform: the AI Workspace, the case back office, the applicant portal, a department's own app, a Teams channel or an external e-services portal. Workspaces choose which of them they use.",
      links: "Deep-link templates say where a record opens in a surface. The AI Workspace, the back office and embedded panels resolve links through this registry, so moving a surface to a new address is a single change here.",
      health: "Each enabled surface with an address is checked every five minutes. A surface going down or coming back raises the solution.health.changed event, which webhooks and automations can react to.",
    },
    probe: {
      all: "Check all now",
      one: "Check now",
      someDown: "Unreachable surfaces: {{count}}",
      allUp: "All {{count}} surfaces answered",
      up: "{{name}} answered in {{ms}} ms",
      down: "{{name}} is not answering",
    },
    stats: {
      surfaces: "Surfaces",
      surfacesHint: "Enabled, out of everything registered",
      healthy: "Answering",
      healthyHint: "At the last health check",
      threads: "Conversations",
      threadsHint: "Started from a surface this month",
      gated: "Behind a feature flag",
      gatedHint: "Rolled out gradually or switched off by flag",
    },
    fields: {
      kind: "Kind",
      deepLinks: "Deep links",
      flag: "Feature flag",
      flagKey: "Flag key",
      keyHint: "Lowercase letters, digits and dashes. Used in threads and links; it cannot be changed later.",
      baseUrl: "Base URL",
      baseUrlHint: "Where the surface lives. Relative deep-link templates are appended to it.",
      baseUrlBuiltIn: "Built-in surfaces follow the platform's configuration for this environment.",
      healthUrl: "Health check URL",
      healthUrlHint: "Optional. Without it the base URL is checked; channels are checked only when this is set. 401 and 403 count as up.",
      handoff: "Single sign-on handoff",
      handoffHint: "Staff opening this surface from another one arrive signed in (one-time code). Turn off for sites outside the platform.",
      adapter: "Adapter key",
      adapterHint: "The server-side adapter that carries conversations to and from this surface.",
      icon: "Icon",
      iconHint: "A lucide icon name, e.g. AppWindow, Globe, MessagesSquare.",
      sortOrder: "Sort order",
      sortOrderHint: "Lower numbers come first in menus and link lists.",
      enabledHint: "Disabled surfaces disappear from menus and links immediately.",
    },
    kind: { WebApp: "Web app", Embedded: "Embedded panel", Channel: "Channel", ExternalPortal: "External portal" },
    kindHint: {
      WebApp: "A platform web application staff or applicants sign in to.",
      Embedded: "The assistant panel inside another application.",
      Channel: "A messaging channel such as Teams or e-mail.",
      ExternalPortal: "A site run outside the platform, such as an e-services portal.",
    },
    health: {
      title: "Health",
      hint: "Checked every five minutes, and whenever you ask.",
      up: "Up",
      down: "Down",
      unknown: "Not checked yet",
      notProbed: "Not checked",
      noTarget: "Nothing to check: add a health check URL (channels) or a base URL.",
      alerts: "Changes of state are published as solution.health.changed, for webhooks and automations.",
    },
    flag: { missing: "Flag not created", off: "Off", on: "On", partial: "{{pct}}% rollout" },
    create: {
      intro: "Register an application, panel, channel or external portal. Deep links, access and health are configured next.",
      namePlaceholder: "e.g. MOEC e-Services",
      keyInvalid: "2–64 lowercase letters, digits or dashes, starting with a letter.",
      keyTaken: "A solution with this key already exists.",
      urlInvalid: "Use an absolute http(s) address.",
      created: "Solution registered",
      createOpen: "Register and configure",
    },
    tabs: { general: "General", connection: "Connection & health", access: "Access", links: "Deep links", embed: "Embed", settings: "Settings" },
    editor: {
      helpTitle: "Configuring a solution",
      nameRequired: "The solution needs a name.",
      deleted: "Solution deleted",
      builtInHint: "This is a built-in surface: its address follows the environment and it cannot be deleted, but links, access, flag and health are yours to configure.",
      fromPackage: "Installed from the package “{{key}}”. Changes here are kept until the package is installed again.",
      identity: "Identity",
      identityHint: "How the surface is named and shown in menus and link lists.",
      usage: "Usage",
      usageHint: "Workspaces that include this surface and conversations started from it.",
      unused: "No workspace includes it yet. Add it on a workspace's Capabilities tab.",
      connection: "Connection",
      connectionHint: "Where the surface lives and how staff arrive there.",
      permissions: "Required permissions",
      permissionsHint: "People need every selected permission to see this surface and its links. Leave empty for everyone with access to the workspace.",
      noPermissions: "No permissions in the catalogue.",
      flagHint: "Optional. While the flag exists and is off for a person, the surface and its links are hidden from them. A flag that does not exist hides nothing.",
      flagMissing: "No flag with this key exists yet, so nothing is gated. Create it under Settings → Feature flags.",
      manageFlags: "Manage feature flags",
      deleteTitle: "Delete {{name}}?",
      deleteInUse: "Workspaces still using it: {{workspaces}}. They lose its links immediately.",
      deleteBody: "Its links disappear immediately. Conversations started from it are kept.",
    },
    settings: {
      title: "Settings",
      hint: "Surface-specific settings as JSON, read by the surface or its adapter.",
      notObject: "Settings must be a JSON object.",
      invalid: "Fix the settings JSON before saving.",
      seedVersion: "seedVersion is maintained by the platform for built-in surfaces; leave it as it is.",
    },
    links: {
      title: "Deep-link templates",
      hint: "Where a record opens in this surface. Placeholders are filled in for the record and the person, and every link is checked against what they may see.",
      add: "Add a link",
      empty: "No deep links",
      emptyHint: "Without links, records never point into this surface.",
      label: "Label",
      labelAr: "Label (Arabic)",
      scope: "Record",
      template: "Template",
      placeholders: "Insert:",
      preview: "Preview:",
      fixFirst: "Links to fix: {{count}}",
      problem: {
        key: "The key must be 2–64 lowercase letters, digits or dashes, starting with a letter.",
        duplicate: "Another link already uses this key.",
        label: "Give the link a label.",
        scope: "Choose what the link points at.",
        template: "Enter a template.",
        relative: "Start with / (relative to the base URL) or use an absolute http(s) address.",
        placeholder: "{{name}} can't be filled in for a “{{scope}}” link.",
      },
    },
    scope: { home: "Home", workspace: "Workspace", case: "Case", matter: "Matter", document: "Case document", proposal: "AI proposal", thread: "Conversation", artifact: "Artifact", assistant: "Assistant" },
    embed: {
      react: "Embed in a React application",
      reactHint: "Inside an app wrapped in ApiProvider and AuthProvider. The conversation is filed under this surface and reused on the next visit.",
      rest: "Any other stack",
      restHint: "The same calls over HTTP with a staff token, for OutSystems, low-code tools or native apps.",
      disabled: "This surface is disabled: conversations cannot be opened from it until it is enabled.",
    },
  },
  workspaces: {
    title: "Workspaces",
    subtitle: "A workspace is a department or function inside the AI Workspace: its assistants, knowledge, playbooks and members, with its own budget and review queue.",
    newWorkspace: "New workspace",
    searchPlaceholder: "Search workspaces…",
    emptyTitle: "No workspaces yet",
    emptyBody: "Create the first workspace to give a department its own assistants, knowledge collections and members.",
    noMatch: "No workspace matches the filter.",
    notFound: "Workspace not found",
    notFoundBody: "There is no workspace with key “{{key}}”. It may have been deleted or renamed.",
    help: {
      what: "Workspaces partition the AI Workspace by department. Everything a member sees — assistants, knowledge, playbooks, threads — is scoped by the workspace they picked.",
      membership: "Membership comes from platform roles (everyone with one of the listed roles) or from explicit members added here. Owners can manage members from the AI Workspace itself.",
      budget: "The monthly budget caps model spend across all assistants of the workspace; the Overview shows the running total and warns at 80%.",
    },
    kind: { Department: "Department", Function: "Function" },
    tabs: { general: "General", capabilities: "Capabilities", modules: "Space design", members: "Members", activity: "Activity" },
    stats: {
      workspaces: "Workspaces",
      workspacesHint: "Enabled in the AI Workspace switcher",
      members: "Members",
      membersHint: "Staff with access, via roles or explicit membership",
      threadsWeek: "Threads this week",
      threadsWeekHint: "Conversations started in the last 7 days",
      spendMonth: "Spend this month",
      pendingHint: "{{count}} proposals waiting for review",
    },
    metrics: {
      assistants: "Assistants",
      members: "Members",
      threads: "Threads",
      thisWeek: "{{count}} this week",
      thisWeekLabel: "This week",
      spendMonth: "Spend / month",
      noBudget: "no budget",
      pending: "To review",
      delegatedRuns: "Agent runs",
      artifacts: "Artifacts",
      roles: "Roles",
    },
    fields: {
      keyHint: "Lower-case identifier used in URLs and packages. Cannot be changed later.",
      kind: "Kind",
      parent: "Parent workspace",
      parentHint: "Optional. A sub-workspace inherits nothing automatically; the parent is used for grouping in switchers and reports.",
      icon: "Icon",
      iconHint: "Lucide icon name, e.g. Scale, Landmark, Gavel.",
      color: "Colour",
      sortOrder: "Sort order",
      enabledHint: "Disabled workspaces stay in the console but disappear from the AI Workspace switcher.",
      monthlyBudget: "Monthly budget (USD)",
      monthlyBudgetHint: "0 means no cap. Assistants stop answering once the budget is exhausted until the month rolls over.",
      defaultAssistant: "Default assistant",
      defaultAssistantHint: "Opens when a member starts a new conversation without picking an assistant.",
      defaultAssistantFirst: "First assistant in the list",
    },
    editor: {
      helpTitle: "Editing a workspace",
      identity: "Identity",
      identityHint: "How the workspace appears in the AI Workspace switcher and on the Home page.",
      budget: "Budget & usage",
      budgetHint: "Live figures for the current month.",
      summary: "{{assistants}} assistants · {{members}} members · {{threads}} threads",
      fromPackage: "Installed from package {{key}}",
      openInWorkspace: "Open in AI Workspace",
      nameRequired: "A name is required.",
      deleteTitle: "Delete workspace?",
      deleteBody: "“{{name}}” will be removed. Threads, artifacts and proposals created in it are kept but no longer reachable from the switcher.",
      deleted: "Workspace deleted.",
    },
    capabilities: {
      assistants: "Assistants",
      assistantsHint: "Published assistants members can talk to. The first one is the default unless set otherwise.",
      noAssistants: "No assistants yet — members will see an empty workspace.",
      knowledge: "Knowledge collections",
      knowledgeHint: "Collections the assistants of this workspace may search. Platform-wide collections are always available.",
      playbooks: "Playbooks",
      playbooksHint: "Internal services (matters) that can be started from chat with /playbook.",
      noPlaybooks: "No playbooks — the assistants can answer and draft but not open matters.",
      solutions: "Solutions",
      solutionsHint: "Department surfaces linked from the AI Workspace (back office, portals, embedded apps).",
    },
    members: {
      title: "Members",
      hint: "Everyone with one of the workspace roles is a member automatically; explicit members are added here or by workspace owners.",
      workspaceRoles: "Member roles",
      workspaceRolesHint: "Platform roles whose holders are members of this workspace.",
      roleMembers: "Members via roles",
      roleMembersHint: "Counted from users holding one of the roles above.",
      roleMembersSave: "Save to recount",
      effective: "{{count}} members in total ({{explicit}} added explicitly).",
      add: "Add member",
      addHint: "Search the staff directory. External users cannot be added to workspaces.",
      searchPlaceholder: "Search by name or e-mail…",
      owner: "Owner",
      ownerHint: "Owners manage members and automations of the workspace from the AI Workspace.",
      promote: "Make owner",
      demote: "Remove owner",
      added: "{{name}} added.",
      removed: "{{name}} removed.",
      addedBy: "Added by {{name}} {{when}}",
      until: "Until {{date}}",
      emptyTitle: "No explicit members",
      emptyBody: "Members currently come from roles only. Add someone here to grant access without a role.",
    },
    activity: {
      since: "Since {{date}}",
      threads: "Threads per day",
      threadsHint: "New conversations by day over the last 30 days.",
      spend: "Spend by assistant",
      spendHint: "Model cost this month attributed to each assistant.",
      users: "Most active members",
      usersHint: "By messages sent in the last 30 days.",
      userStats: "{{threads}} threads · {{messages}} messages",
    },
    create: {
      intro: "Give the department a name and pick a kind. You can add assistants, knowledge and members right after.",
      namePlaceholder: "e.g. Economic Policies",
      keyInvalid: "Use 2–64 lower-case letters, digits and hyphens, starting with a letter.",
      keyTaken: "This key is already in use.",
      createOpen: "Create and open",
      created: "Workspace created.",
    },
    picker: {
      selected: "{{count}} selected",
      unknown: "Unknown — no longer available",
    },
  },
  assistants: {
    title: "Assistants",
    subtitle: "Conversational assistants members talk to in the AI Workspace. Each one has a persona, delegates work to agents, searches knowledge and runs under guardrails.",
    newAssistant: "New assistant",
    searchPlaceholder: "Search assistants…",
    emptyTitle: "No assistants yet",
    emptyBody: "Create an assistant, give it a persona and add it to a workspace so members can start chatting.",
    noMatch: "No assistant matches the filter.",
    help: {
      what: "An assistant is a versioned definition: system prompt, delegates, tools, knowledge, model routing and guardrails. Workspaces reference assistants by key.",
      versions: "Drafts are edited freely; staging opens a change set that publishes the version on release. Published versions are read-only — create a new version to change them.",
      test: "The test bench chats with the exact version you are editing, before it is published, using your own permissions.",
    },
    filters: { unpublished: "Never published" },
    stats: {
      total: "Assistants",
      totalHint: "Distinct assistant keys, all versions",
      published: "Published",
      publishedHint: "Live in at least one workspace",
      drafts: "Drafts",
      draftsHint: "Versions being edited or in review",
      threadsMonth: "Threads this month",
      threadsMonthHint: "Spend {{spend}} this month",
    },
    columns: {
      capabilities: "Capabilities",
      capabilitiesValue: "{{delegates}} delegates · {{knowledge}} collections",
      model: "Model",
      usage: "Usage",
    },
    tabs: { persona: "Persona", capabilities: "Capabilities", model: "Model & guardrails", test: "Test bench", code: "Code", versions: "Versions" },
    tier: { Fast: "Fast", Reasoning: "Reasoning", Vision: "Vision" },
    tierHint: { Fast: "Low latency for chat and drafting.", Reasoning: "Slower, stronger on analysis and multi-step tasks.", Vision: "Understands images and scanned documents." },
    profile: { Standard: "Standard", Strict: "Strict", Minimal: "Minimal" },
    profileHint: { Standard: "PII pseudonymised, citations required, tool calls logged.", Strict: "Standard plus reviewer approval on every proposal and no external tools.", Minimal: "Logging only — for internal drafting assistants on public material." },
    sideEffect: { Read: "Read only", Personal: "Personal", Confirm: "Confirm", Reviewed: "Reviewed" },
    sideEffectHint: { Read: "Search, look up and draft. Nothing changes outside the conversation.", Personal: "Changes the member's own layout or draft at once, with Undo. Nothing shared changes.", Confirm: "May act after the member confirms a card in chat.", Reviewed: "May propose changes that land in the Inbox for a reviewer." },
    persona: {
      identity: "Identity",
      identityHint: "How the assistant presents itself in the switcher and the chat header.",
      prompt: "System prompt",
      promptHint: "Instructions the model follows on every turn. Describe the role, the tone, what to refuse and how to cite sources.",
      promptPlaceholder: "You are the Competition assistant of the Ministry of Economy…",
      chars: "{{count}} characters",
      greeting: "Greeting",
      greetingHint: "Shown at the top of a new conversation.",
      disclaimer: "Disclaimer",
      disclaimerHint: "The line under the message box: what the answers are and are not for this department (e.g. \"information, not legal advice\"). Leave empty for the platform's generic line.",
      greetingEn: "Greeting (English)",
      greetingAr: "Greeting (Arabic)",
      suggested: "Suggested prompts",
      suggestedHint: "Up to six starters shown on the empty chat. Icons are Lucide names.",
      noSuggested: "No suggested prompts — the empty chat shows the greeting only.",
    },
    capabilities: {
      delegates: "Delegates",
      delegatesHint: "Agents the assistant may hand work to. Their runs attach to the thread and their proposals go through review.",
      tools: "Tools",
      toolsHint: "Functions the assistant may call directly. The side-effect ceiling below still applies.",
      allTools: "All tools",
      allToolsHint: "Every registered tool up to the side-effect ceiling. New tools are picked up automatically.",
      toolCount: "{{count}} tools",
      knowledge: "Knowledge collections",
      knowledgeHint: "Collections searched for citations. Workspace collections are added on top at run time.",
      playbooks: "Playbooks",
      playbooksHint: "Internal services the assistant may start with /playbook.",
    },
    model: {
      routing: "Model routing",
      routingHint: "Which tier and provider the gateway selects for this assistant.",
      guardrails: "Guardrails",
      guardrailsHint: "Budget, review and safety settings applied on every turn.",
      alwaysOn: "Always on: PII pseudonymisation, prompt-injection screening, audit trail of every tool call.",
    },
    fields: {
      keyHint: "Lower-case identifier used by workspaces and packages.",
      tagsHint: "Free-form labels for filtering.",
      allowAttachments: "Allow attachments",
      allowVoice: "Allow voice input",
      enabledHint: "Disabled assistants stay published but are hidden from members.",
      connectorOps: "Connector operations",
      connectorOpsHint: "Outbound integration calls the assistant may make, as connector.operation keys.",
      maxSideEffect: "Side-effect ceiling",
      maxSideEffectHint: "The most consequential class of tool this assistant may use.",
      modelTier: "Model tier",
      modelTierHint: "The gateway picks the active route of this tier.",
      modelId: "Model override",
      modelIdHint: "Optional exact model id; leave empty to follow the tier route.",
      provider: "Provider",
      providerHint: "Pin to one provider for data-residency reasons.",
      providerDefault: "Gateway default",
      temperature: "Temperature",
      temperatureHint: "0 is deterministic; above 0.7 answers vary noticeably.",
      maxOutputTokens: "Max output tokens",
      historyWindow: "History window",
      historyWindowHint: "Messages kept verbatim; older turns are summarised.",
      turnTokenBudget: "Token budget per turn",
      turnTokenBudgetHint: "Hard stop for one turn including tool calls.",
      monthlyBudget: "Monthly budget (USD)",
      monthlyBudgetHint: "0 means the workspace budget alone applies.",
      guardrailProfile: "Guardrail profile",
      reviewerRole: "Reviewer role",
      reviewerRoleHint: "Who reviews proposals made through this assistant.",
      reviewerRoleDefault: "Workspace default",
      flagKey: "Feature flag",
      flagKeyHint: "Optional flag that must be on for the assistant to appear.",
    },
    lint: {
      name: "Name is required.",
      prompt: "System prompt is empty — the assistant will answer with defaults only.",
      promptLong: "System prompt is very long; consider moving reference material to a knowledge collection.",
      noCapabilities: "No delegates, tools or knowledge — the assistant can only chat.",
      temperature: "Temperature above 1.0 makes answers unreliable.",
      tokens: "Token budget per turn is lower than the max output tokens.",
      history: "History window below 4 loses context quickly.",
      reviewer: "Reviewed tools are allowed but no reviewer role is set; the workspace default will be used.",
    },
    test: {
      title: "Test bench",
      hint: "Chatting with v{{version}} ({{status}}) using your own permissions.",
      badge: "Test",
      pinned: "Pinned to v{{version}}",
      newThread: "New test conversation",
      recent: "Recent test conversations",
      none: "No test conversations yet.",
      workspace: "Workspace context",
      workspaceHint: "Knowledge and playbooks of this workspace are added, as they would be for a member.",
      savesFirst: "Save your changes first — the test bench chats with the saved version.",
      emptyTitle: "Start a test conversation",
      emptyBody: "Messages here never appear in the AI Workspace. Use it to check tone, citations and tool behaviour before staging.",
    },
    code: {
      hint: "The full definition as JSON. Apply to load it into the editor; invalid JSON is rejected.",
      apply: "Apply JSON",
      applied: "JSON applied to the editor.",
    },
    builder: {
      helpTitle: "Assistant Builder",
      help: { capabilities: "Delegates run as agents with their own guardrails; tools are direct function calls bounded by the side-effect ceiling." },
      summary: "{{delegates}} delegates · {{tools}} tools · {{knowledge}} collections · used by {{workspaces}} workspaces",
      allTools: "all",
      notFound: "Assistant not found",
      notFoundBody: "There is no assistant with key “{{key}}”.",
      notInWorkspace: "This assistant is not in any workspace yet — members cannot reach it.",
      stageTitle: "Stage “{{name}}” for release",
      stageBody: "A change set is opened with this version. It publishes on release and becomes the version members talk to.",
    },
    create: {
      intro: "Start blank or duplicate an existing assistant, then shape its persona and capabilities.",
      namePlaceholder: "e.g. Legal Affairs assistant",
      source: "Start from",
      blank: "Blank",
      blankHint: "Empty persona, no capabilities.",
      duplicate: "Duplicate",
      duplicateHint: "Copy the latest version of an existing assistant.",
      keyInvalid: "Use 2–64 lower-case letters, digits and hyphens, starting with a letter.",
      keyTaken: "This key is already in use.",
      createOpen: "Create and open",
      created: "Assistant created.",
    },
  },
  agents: {
    title: "Agents",
    subtitle: "Specialist agents that draft, review and analyse on matters and in conversations. Configurable agents are defined here without code; the compiled competition agents are listed alongside.",
    newAgent: "New agent",
    searchPlaceholder: "Search agents…",
    emptyTitle: "No agents yet",
    noMatch: "No agent matches the filter.",
    compiledHint: "Implemented in code (the competition agents). Their prompts, evaluations and cost are governed in AI Governance.",
    inCode: "In code",
    help: {
      what: "A configurable agent is a versioned definition: instructions ending with an \"Output sections:\" list, a task template, the knowledge it reads, the tools it may call, the structured output and what an approval does. Playbook stages, assistants and automations invoke it by key.",
      compiled: "Compiled agents are implemented in code and cannot be edited here; open AI Governance for their prompts, evaluations and cost.",
      test: "The test bench runs the exact version you are editing on a real matter or a brief, through every guardrail, and records the draft on the run – nothing is filed and no proposal is created.",
    },
    kind: { Configurable: "Configurable", Compiled: "Compiled" },
    filters: { unused: "Not used anywhere" },
    stats: {
      total: "Agents",
      totalHint: "{{compiled}} of them implemented in code",
      configurable: "Configurable",
      configurableHint: "{{drafts}} with a draft in progress",
      runsMonth: "Runs this month",
      runsMonthHint: "Spend {{spend}} this month",
      acceptance: "Acceptance",
      acceptanceHint: "Of {{proposals}} proposals this month",
    },
    columns: { kind: "Kind", usedBy: "Used by", month: "This month", monthValue: "{{runs}} run(s) · {{spend}}", lastRun: "Last run" },
    usage: { stages: "{{count}} stage(s)", assistants: "{{count}} assistant(s)" },
    tabs: { instructions: "Instructions", grounding: "Knowledge & tools", steps: "Steps & sub-agents", output: "Output & approval", model: "Model & budget", test: "Test bench", evaluation: "Go-live tests", code: "Code", versions: "Versions" },
    evaluation: {
      casesTitle: "Test cases",
      casesHint: "An input for the agent (a brief or a matter number) and what its draft must or must not say. Up to 12 cases.",
      addCase: "Add test case", newCase: "Test case {{n}}", remove: "Remove test case", name: "Test case name",
      noCases: "No test cases yet", noCasesBody: "Add at least one case: the evaluation, and so the release, needs it.",
      brief: "Brief", briefPlaceholder: "What an officer would ask the agent…", matter: "Matter number", matterHint: "Optional: run on this matter.",
      contains: "The draft must say", notContains: "The draft must not say", listHint: "One phrase per line.",
      fields: "Output fields that must be filled", fieldsHint: "Keys of the output schema, separated by commas.", maxLength: "Maximum length (characters)",
      redTeamTitle: "Red-team pack",
      redTeamHint: "Run on every evaluation. The agent passes when it does not reveal its instructions or propose an approval, lets no raw personal data through and gives no advice outside its job.",
      probes: { injection: "Prompt injection", "personal-data": "Personal-data leakage", "off-scope": "Off-scope request" },
      gateTitle: "Go-live gate",
      gateHint: "Releases will not approve this version until its latest evaluation passed. Each case runs in the sandbox: nothing is filed or sent.",
      run: "Run evaluation of v{{version}}", running: "Running {{count}} sandboxed runs…",
      passed: "Passed", failed: "Failed", never: "This version has not been evaluated yet.",
      passedToast: "Evaluation passed: this version can be released.", failedToast: "Evaluation failed: fix the agent or the cases and run it again.",
      score: "{{passed}}/{{total}} cases · {{red}}/{{redTotal}} red-team",
      runBy: "Run by {{name}} {{when}} in {{seconds}} s",
      history: "Earlier runs of this version",
      kind: { case: "Case", redteam: "Red team" },
    },
    onApprove: {
      title: "After approval",
      intro: "Actions run once a reviewer approves the draft. Values come from the case and the approved draft.",
      add: "Add action", none: "Nothing else happens on approval.", addValue: "Add value", field: "Field", value: "Value", processTitle: "Title of the new case",
      types: { record_create: "Create a record", letter_send: "Send a letter", process_start: "Start another process" },
      choose: { record_create: "Record type…", letter_send: "Letter template…", process_start: "Service…" },
      map: { record_create: "Record fields", letter_send: "Letter values", process_start: "Intake answers" },
      placeholders: "Placeholders you can use:",
    },
    steps: {
      title: "Steps", hint: "Break the work into steps. They run in order, each sees what the earlier ones produced, and the agent writes its draft last.",
      add: "Step", newTitle: "Step {{n}}", empty: "One step", emptyBody: "Without steps the agent works the task in one go. Add steps for work that needs gathering, checking or a specialist first.",
      instruction: "What to do in this step", instructionPlaceholder: "e.g. Work out the sector's share of GDP and employment from the official figures.",
      doneBy: "Done by", thisAgent: "This agent", tools: "Tools for this step", toolsHint: "None selected: the agent's own tools.", handedTo: "Handed to {{agent}}",
      final: "Step {{n}}: the agent writes the draft from the steps' results, then a person reviews it.",
      delegates: "Sub-agents it may ask", delegatesHint: "The agent can hand a sub-task to these agents while it works (as a tool). Their drafts come back to it; only its own draft goes to review.",
      moveUp: "Move up", moveDown: "Move down", remove: "Remove",
    },
    instructions: {
      prompt: "Instructions",
      promptHint: "The agent's role, method and standards, sent as the system prompt on every run. End with an \"Output sections:\" list of \"- Heading\" lines – every draft follows those headings in order.",
      promptPlaceholder: "You are the policy drafter of the Ministry of Economy…\n\nOutput sections:\n- Background\n- Options\n- Recommendation",
      sectionsFound: "{{count}} output section(s) found",
      noSections: "No \"Output sections:\" list – drafts will not have a fixed structure.",
      task: "Task template",
      taskHint: "What the agent is asked on each run. Placeholders are filled from the matter or the brief; conditional blocks keep only the text that applies at this stage, playbook or trigger. Click a token to insert it.",
      placeholders: "Placeholders",
      blocks: "Conditional blocks",
      appended: "When the template does not place them, the matter's facts, the brief and the document list are appended automatically, followed by the sources retrieved before the run.",
      unknownTokens: "Unknown placeholders are left empty:",
      unclosed: "Blocks opened but not closed:",
      identity: "Identity",
      identityHint: "How the agent appears in the catalogue, on proposals and in the matter timeline.",
      descriptionHint: "One sentence on what the agent produces – assistants read it when deciding to delegate.",
      tagsHint: "The workspace key first (used for filtering), then free-form labels.",
      enabledHint: "Disabled agents stay published but are not run by workflows or assistants.",
      usage: "Where it runs",
      usageHint: "Playbook stages and assistants that invoke this agent's key, from the published definitions.",
      unused: "Nothing invokes this agent yet. Add it to a workflow stage in Workflow Studio or to an assistant's delegates.",
      stages: "Playbook stages",
      assistants: "Assistants",
      fromPackage: "Imported from package {{key}}.",
    },
    grounding: {
      knowledge: "Knowledge",
      knowledgeHint: "Where the agent's sources come from. The closest passages are retrieved before the model starts, so even a run without tools is grounded and cited.",
      source: { matter: "The matter's knowledge", "case-documents": "The matter's documents", "legal-corpus": "Competition law corpus" },
      sourceHint: {
        matter: "Collections of the matter's playbook and selected option (for example the law of the grievance type), or of the workspace when the agent is asked from a conversation.",
        "case-documents": "Documents filed on the matter, searchable with search_case_documents.",
        "legal-corpus": "Federal Decree-Law No. 36 of 2023 on competition and its implementing decisions.",
      },
      collections: "Named collections",
      collectionsHint: "Always searched, whatever the matter.",
      tools: "Tools",
      toolsHint: "Deterministic functions the model may call during a run. Tools that need a matter are withheld when the agent runs on a brief.",
      auto: "Added from the knowledge settings",
      autoNone: "None – turn on a knowledge source to give the agent search.",
      needsCase: "needs a matter",
      connectorOpsHint: "Outbound integration calls the agent may make, as connector.operation keys.",
    },
    output: {
      deliverable: "Deliverable",
      deliverableHint: "Every run returns the same envelope; the reviewer sees it as a proposal.",
      kind: "Proposal kind",
      kindHint: "Shown to reviewers and used by the Inbox filters (the output schema's title).",
      sections: "Sections (from the instructions)",
      noSections: "Add an \"Output sections:\" list at the end of the instructions to fix the draft's structure.",
      envelope: "Always returned",
      envelopeHint: "Title and summary in English and Arabic, the draft in markdown with [S#] citations, up to six key points, open questions, urgency and confidence. The structured data is optional.",
      structured: "Structured data",
      structuredHint: "Optional JSON Schema for data the draft carries alongside the text (a timeline, a scorecard, a list of clauses). Reviewers see it with the draft.",
      documentOnly: "No structured data – the agent returns a document only.",
      field: "Field",
      type: "Type",
      approval: "Approval",
      approvalHint: "Who reviews the draft and what happens when they approve it. Nothing is filed or sent without that approval.",
      reviewer: "Reviewer",
      reviewerHint: "\"Role of the current stage\" routes the proposal to whoever the playbook has hold the stage's open task.",
      reviewerDefault: "The officer who asked, or the matter's lead",
      actions: "On approval",
      actionsHint: "Tried in order; the first that applies to the draft is proposed (filing needs a matter, sending questions needs open questions and a recipient).",
      action: { matter_document_file: "File the draft on the matter", matter_rfi: "Send the open questions", matter_information_request: "Send the open questions", none: "Record the draft only" },
      recordOnly: "No action – approving records the draft.",
      moveUp: "Move up",
      moveDown: "Move down",
    },
    model: {
      routingHint: "Which tier the gateway selects for this agent. Drafting agents usually need Reasoning.",
      temperatureHint: "Keep it low (0.1–0.3) for drafting that must stay close to the sources.",
      maxOutputTokensHint: "Upper bound on the length of the draft, 1,024–65,536.",
      guardrails: "Budget and guardrails",
      guardrailsHint: "Limits applied to every run of this agent.",
      runTokenBudget: "Token budget per run",
      runTokenBudgetHint: "Hard stop for one run including tool calls; 0 means no limit beyond the workspace budget.",
      monthlyBudgetHint: "0 means the workspace budget alone applies.",
      alwaysOn: "Always on: PII pseudonymisation, prompt-injection screening, the citation verifier, an independent quality review and human approval of every output.",
    },
    lint: {
      name: "Name is required.",
      temperature: "Temperature must be between 0 and 1.",
      tokens: "Max output tokens must be between 1,024 and 65,536.",
      budget: "Budgets cannot be negative.",
      runBudget: "The token budget per run is lower than the max output tokens.",
      schema: "The output schema must be a JSON object.",
      action: "Unknown action(s): {{actions}}.",
      rfiFields: "Sending questions needs the intake fields holding the recipient's name and e-mail, e.g. grievantName,email.",
      prompt: "Instructions are empty – they are required before publishing.",
      sections: "The instructions have no \"Output sections:\" list.",
      ungrounded: "No knowledge source – the agent will draft from general knowledge only.",
      placeholders: "Unknown placeholders: {{tokens}}",
      blocks: "Blocks opened but not closed: {{blocks}}.",
    },
    test: {
      title: "Test bench",
      hint: "Runs v{{version}} ({{status}}) in the sandbox with your permissions.",
      mode: { matter: "On a matter", brief: "On a brief" },
      modeHint: { matter: "Reads the matter as a stage run would.", brief: "As if delegated from a conversation." },
      matter: "Matter",
      matterHint: "Recent matters of the playbooks that use this agent.",
      noMatters: "No matter uses this agent's playbooks yet – test it on a brief.",
      note: "Officer's note (optional)",
      noteHint: "Passed as the brief, as a revision request would be.",
      brief: "Brief",
      briefHint: "What an officer would ask in a conversation.",
      briefPlaceholder: "e.g. Draft a policy note on licence-fee waivers for female entrepreneurs.",
      workspaceHint: "Its knowledge collections are used when the agent reads the matter's knowledge.",
      preview: "Preview prompt",
      run: "Run in sandbox",
      queued: "Run queued – the draft appears here when it completes.",
      notStarted: "The run has not started yet. Check that the background worker is running.",
      savesFirst: "Unsaved changes are saved first – the test bench runs the saved version.",
      sandbox: "Sandbox: guardrails, the citation verifier and the quality review apply; nothing is filed, no proposal is created and the matter's timeline is untouched.",
      recent: "My test runs",
      none: "No test runs yet.",
      usage: "{{tokens}} tokens · ${{cost}}",
      status: { Queued: "Queued", Running: "Running", Succeeded: "Done", Failed: "Failed", Cancelled: "Cancelled" },
      emptyTitle: "Try the agent",
      emptyBody: "Pick a matter or write a brief, preview the exact prompt, then run it in the sandbox.",
      previewTitle: "Prompt preview",
      previewHint: "What the run sends before retrieval and tool calls. No model is called.",
      renderedTask: "Task (rendered)",
      showSteps: "Show {{count}} steps",
      hideSteps: "Hide steps",
      confidence: "Confidence {{value}}%",
      reviewer: "Reviewer: {{role}}",
      quality: "Quality {{score}}/100",
      keyPoints: "Key points",
      openQuestions: "Open questions",
      action: "Proposed on approval",
      noAction: "No action would be proposed – approving would record the draft only.",
      unsupported: "Claims the citation verifier could not match to a source",
      sources: "{{count}} source(s)",
      noDraft: "The run completed without a draft.",
    },
    create: {
      intro: "Start blank or duplicate a configurable agent that works, then shape its instructions, knowledge and output.",
      namePlaceholder: "e.g. Tariff impact reviewer",
      source: "Start from",
      blank: "Blank",
      blankHint: "Default task template, reading the matter's knowledge and documents.",
      duplicate: "Duplicate",
      duplicateHint: "Copy the latest version of a configurable agent.",
      keyHint: "Identifier used by workflows, assistants and packages. It cannot be changed later.",
      keyInvalid: "Use 2–64 lower-case letters, digits, dots, underscores or hyphens, starting with a letter.",
      keyTaken: "This key is already in use.",
      createOpen: "Create and open",
      created: "Agent created.",
    },
    builder: {
      helpTitle: "Agent Builder",
      help: { template: "The task template is rendered on every run: the matter, brief and documents placeholders expand to the matter's facts, and stage, playbook and parameter blocks keep only the text that applies." },
      summary: "{{sections}} section(s) · {{knowledge}} knowledge source(s) · {{tools}} tool(s) · {{stages}} stage(s) · {{assistants}} assistant(s)",
      notFound: "Agent not found",
      notFoundBody: "There is no configurable agent with key “{{key}}”. Agents implemented in code are governed in AI Governance.",
      compiledNotice: "This definition configures the compiled agent {{key}}: its code sets the method, so only the model and budget apply.",
      publishedNotice: "This version is live: stages and assistants use it from their next run. Create a new version to change it.",
      stageTitle: "Stage agent “{{name}}” for release",
      stageBody: "A change set is opened with this version. On release it becomes the version every stage and assistant runs.",
    },
  },
  knowledge: {
    title: "Knowledge",
    subtitle: "Collections of documents assistants search and cite. Owned by the platform or by one workspace, with a visibility that decides who may read them.",
    newCollection: "New collection",
    searchPlaceholder: "Search collections…",
    emptyTitle: "No knowledge collections",
    emptyBody: "Create a collection and upload the first documents, or install a department package.",
    noMatch: "No collection matches the filter.",
    notFound: "Collection not found",
    notFoundBody: "There is no collection with key “{{key}}”.",
    scope: { platform: "Platform" },
    help: {
      what: "A collection groups documents (laws, decisions, guidelines, precedents) that are chunked, embedded and searched for citations.",
      visibility: "Public collections are readable by anyone in the AI Workspace, Internal ones by members of the owning workspace, Agents-only ones are searchable but never browsed.",
      sources: "Documents arrive by upload here or in the AI Workspace, or from SharePoint libraries, OneDrive folders and web pages synchronised on a schedule or on demand.",
    },
    visibility: { Public: "Public", Internal: "Internal", AgentsOnly: "Agents only" },
    visibilityHint: {
      Public: "Everyone signed in to the AI Workspace can browse and cite these documents.",
      Internal: "Only members of the owning workspace can browse; assistants of that workspace cite them.",
      AgentsOnly: "Never shown in the Knowledge browser; assistants search and cite them.",
    },
    stats: {
      collections: "Collections",
      collectionsHint: "{{platform}} platform-wide",
      documents: "Documents",
      documentsHint: "Active documents across all collections",
      chunks: "Chunks",
      chunksHint: "Embedded passages available to retrieval",
      errors: "Ingestion errors",
      errorsHint: "Collections whose last ingestion failed",
    },
    card: {
      documents: "{{count}} documents",
      chunks: "{{count}} chunks",
      error: "Ingestion error",
      usedBy: "Used by {{workspaces}} workspaces · {{assistants}} assistants",
      lastDocument: "last document {{when}}",
    },
    fields: {
      keyHint: "Lower-case identifier referenced by workspaces, assistants and packages.",
      owner: "Owner",
      ownerHint: "Platform collections are available to every workspace; workspace collections belong to one department.",
      visibility: "Visibility",
      defaultKind: "Default document kind",
      uploadRoles: "Upload roles",
      uploadRolesHint: "Roles allowed to upload from the AI Workspace. Console users with knowledge write may always upload.",
      enabledHint: "Disabled collections are skipped by retrieval and hidden from browsers.",
    },
    editor: {
      helpTitle: "Editing a collection",
      summary: "{{documents}} documents · {{chunks}} chunks · {{owner}}",
      settings: "Settings",
      settingsHint: "Identity, ownership and who may read or upload.",
      usedBy: "Referenced by",
      usedByHint: "Workspaces and assistants that search this collection.",
      noWorkspaces: "No workspace lists this collection.",
      noAssistants: "No assistant lists this collection.",
      refresh: "Recount",
      ingestionError: "Last ingestion failed: {{error}}",
      deleteTitle: "Delete collection?",
      deleteBody: "“{{name}}” and its documents will be removed from retrieval. Assistants citing it will stop finding these sources.",
      deleted: "Collection deleted.",
    },
    documents: {
      title: "Documents",
      hint: "Everything in the collection, seeded or uploaded. Removing a document deletes its chunks.",
      search: "Search documents…",
      kind: "Kind",
      document: "Document",
      chunks: "Chunks",
      includeInactive: "Show disabled",
      seeded: "Seeded",
      emptyTitle: "No documents",
      emptyBody: "Upload a PDF, DOCX or text file to make it searchable.",
      upload: "Upload document",
      uploadHint: "Into “{{name}}”. Scanned PDFs are OCR-ed; the text is chunked and embedded right away.",
      dropHint: "Choose a file (PDF, DOCX, TXT, MD or image)",
      titleEn: "Title (English)",
      titleAr: "Title (Arabic)",
      issuer: "Issuer",
      issuedOn: "Issued on",
      sourceUrl: "Source URL",
      uploaded: "“{{title}}” ingested — {{chunks}} chunks.",
      removeConfirm: "Remove “{{name}}” and its chunks from the collection?",
      removed: "Document removed.",
      syncedHint: "Filed and kept up to date by this source",
      withdrawn: "Withdrawn",
    },
    sources: {
      title: "Sources",
      hint: "SharePoint libraries, OneDrive folders, web pages, crawled sites and the harvested economic-sources snapshot kept in sync with this collection.",
      add: "Add source",
      editTitle: "Edit “{{name}}”",
      dialogIntro: "Documents found at the source are read, chunked and embedded. Changed files are re-read, and files that leave the source are withdrawn.",
      empty: "No synchronised source. Documents arrive by upload only.",
      saveFirst: "Save the collection before syncing: runs read the saved sources.",
      collectionDisabled: "The collection is disabled, so its sources do not sync.",
      noRecentRun: "No recent run",
      syncNow: "Sync now",
      queued: "“{{name}}” is syncing.",
      removeConfirm: "Remove the source “{{name}}”? Documents it filed stay in the collection until you remove them.",
      keyLocked: "Fixed: the documents this source filed refer to it.",
      test: "Test source",
      apply: "Apply",
      previewFailed: "The source could not be read",
      previewSummary: "{{total}} item(s) found, {{supported}} can be read",
      previewFirst: "showing the first {{count}}",
      unsupported: "Not readable",
      kind: { upload: "Manual uploads", url: "Web pages", crawl: "Crawled site", snapshot: "Harvest snapshot", sharepoint: "SharePoint library", onedrive: "OneDrive folder" },
      kindHint: {
        url: "Public https pages, PDFs or Word files",
        crawl: "A site followed from its start pages",
        snapshot: "Publications harvested with the economic data",
        sharepoint: "A document library of a team site",
        onedrive: "A folder in someone's OneDrive",
      },
      namePlaceholder: { url: "e.g. Statistics methodology pages", crawl: "e.g. Ministry reports library", snapshot: "e.g. Harvested publications", sharepoint: "e.g. Opinions register", onedrive: "e.g. Policy unit papers" },
      fields: {
        urls: "Addresses",
        urlsHint: "One per line, up to 50. Pages are read as text without menus and footers; PDF and Word links are read as files.",
        startUrls: "Start pages",
        startUrlsHint: "One per line, up to 10. Only links on the same site are followed, within its robots.txt, one request at a time.",
        depth: "Link depth",
        depthHint: "0 reads the start pages only; 1 also their links; 2 the links of those.",
        maxItems: "Most items",
        maxItemsHint: "Up to 300 pages and files per run.",
        include: "Keep addresses matching",
        includeHint: "One pattern per line: text the address contains, or a pattern with * (e.g. *.pdf). Empty keeps every page reached.",
        exclude: "Skip addresses matching",
        excludeHint: "Neither kept nor followed (e.g. /news/ or *login*).",
        snapshotCollection: "Snapshot document file",
        snapshotCollectionHint: "The harvested publications packed for this subject (data/economic-sources/documents). A new snapshot is filed on the next run; unchanged documents are skipped.",
        connector: "Microsoft Graph connector",
        connectorHint: "The Connector Builder connection whose app registration may read the site or drive.",
        siteUrl: "Site address",
        library: "Library",
        libraryHint: "The document library's name, usually “Documents”.",
        user: "OneDrive owner",
        userHint: "The owner's e-mail or user principal name.",
        folder: "Folder",
        folderHint: "Leave empty for the whole library or drive.",
        recursive: "Sub-folders",
        recursiveHint: "Include sub-folders",
        documentKind: "File documents as",
        issuerHint: "Shown in citations; defaults to the source's name.",
        schedule: "Schedule",
        scheduleHint: "UAE time. Due sources are picked up within five minutes.",
      },
      schedule: { manual: "Manual only", hourly: "Hourly", daily: "Daily 03:00", weekly: "Mondays 04:00", monthly: "Monthly, 1st 03:00", custom: "Custom (cron)" },
      errors: {
        keyRequired: "Give the source a key.",
        cron: "A cron expression has five fields: minute hour day month weekday.",
        address: "“{{url}}” is not an https address.",
        startUrls: "Give between one and ten start pages.",
        maxItems: "Between 1 and 300.",
      },
    },
    runs: {
      title: "Sync history",
      hint: "Every synchronisation and re-embedding run, with what it filed. Click a run for the item log.",
      reembed: "Re-embed all",
      reembedConfirm: "Re-compute the embeddings of all {{documents}} documents? Use this after the embedding model changes.",
      reembedQueued: "Re-embedding started.",
      reembedAll: "Re-embedding",
      emptyTitle: "No runs yet",
      emptyBody: "Runs appear here when a source syncs or the collection is re-embedded.",
      source: "Source",
      statusLabel: "Status",
      result: "Result",
      when: "When",
      noChanges: "No changes",
      inProgress: "The run is still in progress…",
      noItems: "The run recorded no items.",
      detailSummary: "{{trigger}} · {{who}} · {{when}}",
      seen: "{{count}} item(s) at the source",
      chunks: "{{count}} passage(s) embedded",
      durationMs: "{{value}} ms",
      durationS: "{{value}} s",
      durationMin: "{{value}} min",
      status: { Queued: "Queued", Running: "Running", Succeeded: "Succeeded", PartiallySucceeded: "Partly succeeded", Failed: "Failed" },
      trigger: { Manual: "Manual", Schedule: "Scheduled", Reembed: "Re-embedding" },
      count: { added: "{{count}} added", updated: "{{count}} updated", unchanged: "{{count}} unchanged", removed: "{{count}} withdrawn", failed: "{{count}} failed", reembedded: "{{count}} re-embedded" },
      action: { added: "Added", updated: "Updated", unchanged: "Unchanged", removed: "Withdrawn", skipped: "Skipped", failed: "Failed", reembedded: "Re-embedded" },
    },
    create: {
      intro: "Name the collection and decide who owns it and who may read it. Upload documents right after.",
      namePlaceholder: "e.g. Competition law",
      keyInvalid: "Use 2–64 lower-case letters, digits and hyphens, starting with a letter.",
      keyTaken: "This key is already in use.",
      createOpen: "Create and open",
      created: "Collection created.",
    },
  },
  automations: {
    title: "Automations",
    subtitle: "Rules that react to platform events or run on a schedule: platform automations authored here and released through change sets, workspace automations shared with a department, and the personal watchers staff set up from chat.",
    new: "New automation",
    searchPlaceholder: "Search name, key or owner",
    emptyTitle: "No automations yet",
    emptyBody: "Create a platform or workspace automation here. Staff can also set up personal watchers from any chat in the AI Workspace.",
    noMatch: "No automations match the current filters.",
    neverRan: "Has not run",
    runCount: "{{runs}} runs",
    tabs: { list: "Automations", runs: "Run log", design: "Design", code: "Code", versions: "Versions" },
    stats: {
      platform: "Platform", platformHint: "Authored in the console, released with approval",
      workspace: "Workspace", workspaceHint: "Workspaces using them: {{count}}",
      personal: "Personal watchers", personalHint: "Set up by staff from chat",
      runsWeek: "Runs this week", failedWeek: "{{count}} failed",
    },
    scope: { Platform: "Platform", Workspace: "Workspace", Personal: "Personal" },
    state: { on: "On", paused: "Paused" },
    filters: { on: "On", paused: "Paused", draft: "Draft or in review", failing: "Last run failed" },
    columns: { automation: "Automation", scope: "Scope", when: "When", then: "Then", lastRun: "Last run" },
    runStatus: { Succeeded: "Succeeded", Failed: "Failed", Skipped: "Skipped", DryRun: "Dry run" },
    priority: { Low: "Low", Medium: "Medium", High: "High", Critical: "Critical" },
    setField: { priority: "Priority", summary: "Summary" },
    help: {
      what: "An automation waits for something — an event such as a matter being opened or a deadline at risk, a schedule, a manual start or an inbound webhook — checks an optional condition and then runs up to ten actions in order.",
      scopes: "Platform automations may take any action and go live only through a change set. Workspace automations belong to one department and may notify, run agents and open matters from playbooks. Personal watchers act only for the person who set them up.",
      condition: "The condition is JsonLogic evaluated against the event and the case or matter it concerns (priority, playbook, stage, days remaining…). Leave it empty to run every time.",
      safety: "Automations cannot trigger one another more than three levels deep, and runs started by an automation never start agents or playbooks. A dry run records what would happen without changing anything.",
    },
    create: {
      intro: "Start with a name and where the automation applies; you choose the trigger, condition and actions in the editor.",
      namePlaceholder: "e.g. Escalate missed statutory deadlines",
      keyHint: "Stable identifier used in change sets, packages and the run log.",
      scopeHint: {
        Platform: "Any action; goes live through a change set.",
        Workspace: "Shared with one workspace; notifications, agents and playbooks.",
        Personal: "Acts only for its owner.",
      },
      createOpen: "Create and open",
      created: "Automation created as a draft.",
    },
    trigger: {
      kind: "Runs",
      modes: { event: "When something happens", schedule: "On a schedule", manual: "By hand", webhook: "Inbound webhook", custom: "Custom trigger" },
      event: "Event",
      playbook: "Only for playbook",
      playbookHint: "Leave empty to react to every playbook.",
      anyPlaybook: "Any playbook",
      state: "Only at stage",
      stateHint: "Workflow stage key, e.g. committee-review. Leave empty for every stage.",
      agent: "Only for agent",
      anyAgent: "Any agent",
      onlyMine: "Only cases assigned to the owner.",
      manualHint: "Runs only when started from this page, the API or another platform service.",
      webhookHint: "Runs when an inbound webhook delivers an event for this automation (see Integrations → Webhooks).",
      key: "Trigger key",
      keyHint: "e.g. case.stateEntered, clock.atRisk, payment.received.",
      config: "Trigger configuration (JSON)",
    },
    schedule: {
      presets: { weekdays: "Working days 07:00", daily: "Every day 07:00", monday: "Mondays 07:00", monthly: "1st of the month 06:00", quarterly: "Quarterly (5th) 06:00", yearly: "Yearly (15 Jan) 06:00" },
      cron: "Cron expression",
      cronHint: "Five fields: minute hour day-of-month month weekday.",
      timeZone: "Time zone",
      gst: "UAE time (GST)",
      next: "Next runs",
    },
    actions: {
      type: "Action",
      add: "Add action",
      empty: "No actions yet — add at least one before releasing.",
      up: "Move up",
      down: "Move down",
      notAllowed: "{{scope}} automations cannot take this action.",
      needsCase: "Needs a case or matter: use it with an event about one.",
    },
    fields: {
      to: "Recipient", toHint: "When empty: the assignee of the case or matter, or whoever set the automation up when there is none. Escalations go to the Director.",
      text: "Message", textHint: "Optional; a standard message is used when empty.", textPlaceholder: "{{eventLabel}}: {{caseNumber}} — {{title}}",
      placeholders: "Insert",
      agent: "Agent",
      brief: "Brief for the agent", briefHint: "What the agent should do; placeholders are filled in when it runs.",
      playbook: "Playbook",
      parameters: "Playbook parameters", parametersHint: "Values may use placeholders such as {{previousQuarter}} or {{previousMonth}}.",
      paramName: "name", addParam: "Add parameter",
      title: "Matter title", titleHint: "Optional; defaults to the playbook name and the period.",
      priority: "Raise priority to",
      trigger: "Transition", triggerHint: "Name of the workflow transition to fire.",
      field: "Field", value: "Value",
      connector: "Connector", operation: "Operation", input: "Input (JSON)",
      connectorEmpty: "No published connector yet – build one under Integrations → Connectors.",
      readOnlyOnly: "{{scope}} automations can only read: operations that change data are greyed out.",
      writes: "changes data",
      connectorInput: "Values", connectorInputHint: "Leave a value empty to use its default. Values may use placeholders such as {{previousQuarter}}.",
      pickConnectorFirst: "Choose a connector and an operation to fill in its values.",
      noParameters: "This operation takes no values.",
      defaultValue: "Default: {{value}}",
      saveTo: "Save the result", saveToNone: "Keep it in the run log only", saveToMatter: "File on the matter as a workbook (XLSX)",
      saveToNoTable: "This operation returns no table, so there is nothing to file.",
      recordType: "Record type", template: "Letter template", service: "Service to start",
      fields: "Record fields", fieldsHint: "Each record field and its value; values may use placeholders such as {{caseNumber}}.",
      values: "Letter values", valuesHint: "Extra values the template fills in, by name.",
      intake: "Intake answers", intakeHint: "Each field of the service's intake form and its value.",
      linkCase: "Link to the case", linkCaseHint: "true or false; true by default when there is a case.",
      workbookTitle: "Workbook title", workbookTitleHint: "Optional; defaults to the operation and the period. Placeholders are filled in when it runs.",
      subscription: "Webhook subscription", subscriptionEmpty: "No enabled webhook subscription – add one under Access → Webhooks.",
      workspace: "Workspace", workspaceHint: "Leave empty to use the automation's own workspace.", days: "Period (days)", daysHint: "How far back and ahead the digest looks: 1 to 31 days, 7 by default.",
      throttle: "Throttle (minutes)", throttleHint: "Skip repeat runs for the same case within this window; 0 runs every time.",
      enabledHint: "Whether the automation starts enabled once released. It can be paused at any time without a release.",
      invalidJson: "Not valid JSON.",
    },
    recipient: { default: "Default", owner: "The owner", assignee: "The assignee", members: "The workspace members", roles: "Everyone with the role", person: "A specific person (user:…)" },
    vars: {
      priority: "Priority", serviceKey: "Playbook or service", state: "Stage", status: "Status", workspaceKey: "Workspace", assigneeUserId: "Assignee (user id)",
      isMatter: "Is a matter", title: "Title", daysRemaining: "Days remaining (deadlines)", clock: "Deadline", category: "Document category",
      fromApplicant: "Uploaded by the applicant", agentKey: "Agent (drafts)", confidence: "Draft confidence", urgency: "Draft urgency", triggerSource: "Started from",
    },
    editor: {
      helpTitle: "Automation editor",
      notFound: "Automation not found",
      notFoundBody: "There is no automation with the key \"{{key}}\".",
      nameRequired: "Give the automation a name.",
      when: "When", whenHint: "What starts the automation.",
      onlyIf: "Only if", onlyIfHint: "Optional condition on the event and the case or matter it concerns.",
      noCondition: "No condition — runs every time.",
      then: "Then", thenHint: "Actions run in order; a failed action does not stop the next.",
      settings: "Settings", settingsHint: "Name, scope and how often it may run.",
      summary: "In plain words", summaryHint: "Checked by the platform as you edit.", checking: "Checking…",
      ready: "Ready to release.",
      activity: "Activity", runs: "Runs", failures: "Failures", nextRun: "Next run", owner: "Owner",
      fromChat: "Set up from a chat in the AI Workspace.",
      pause: "Pause", resume: "Resume", paused: "Automation paused.", resumed: "Automation resumed.",
      openDraft: "Open draft v{{version}}",
      liveNotice: "Version {{version}} is live; this draft takes over once its release is approved.",
      openLive: "Open the live version",
      personalNotice: "A personal watcher of {{owner}}, managed from the AI Workspace. Administrators can pause it, run it and read its log here.",
      stageTitle: "Automation: {{name}}",
      stageBody: "The draft is saved and added to a change set. A second administrator approves the release before it goes live.",
      newVersionBody: "Creates draft v{{version}} as a copy of this version. The live version keeps running until the new one is released.",
      codeHint: "The automation as JSON — what change sets and packages carry. Apply to load edits into the editor.",
    },
    run: {
      title: "Run",
      intro: "Run the live version now. Event automations need the case or matter the event would concern; scheduled and manual ones do not.",
      case: "Case or matter",
      caseHint: "Its number or ID. Optional; required for actions on a case (notes, assignment, escalation).",
      caseInvalid: "Enter a case or matter number such as GRV-2026-000001, or its ID.",
      dryRun: "Dry run",
      dryRunHint: "Records what each action would do without changing anything.",
      test: "Dry run",
      now: "Run now",
      recent: "Recent:",
      openLog: "Open the run log",
    },
    runs: {
      empty: "No runs yet",
      emptyHint: "Every run — real or dry — is recorded here with what each action did.",
      trigger: "Trigger", case: "Case", actions: "Actions", duration: "Duration", when: "When",
      conditionFailed: "The condition was not met.",
      noActions: "No actions ran.",
    },
  },
  feedback: {
    title: "Reported issues",
    subtitle: "What users reported from the screen they were on, with a picture of the page, its address and the build they used.",
    empty: "No issues have been reported.",
    all: "All",
    reporter: "Reported by",
    page: "Page",
    screenshot: "Screen",
    note: "Note for the reporter's record",
    categories: { Wording: "Wording", Layout: "Layout", Error: "Error", Suggestion: "Suggestion", Other: "Other" },
    statuses: { New: "New", InProgress: "In progress", Resolved: "Resolved", Dismissed: "Dismissed" },
    setStatus: "Mark as {{status}}",
    updated: "Status updated.",
    allSections: "All sections",
    unclassified: "Not yet classified",
    classes: { Critical: "Critical", Major: "Major", Minor: "Minor" },
    areas: { Threshold: "Notification threshold", Deadline: "Statutory deadline", Fee: "Fee" },
    sections: {
      EC: "Economic concentrations", AC: "Complaints", WB: "Infringement reports", EX: "Exemptions", EA: "Amendments to exemptions", GR: "Grievances", LE: "Legal enquiries",
      RQ: "Requests to other bodies", DB: "Data Bank and market studies", DW: "Deal Watch", ST: "Statistics", AD: "Advocacy", PW: "Platform-wide",
    },
    disagreement: "Net0 proposes {{proposed}}; the Department's class, {{applies}}, applies until the Department answers.",
    history: {
      Classified: "{{by}} classified it {{cls}}",
      Upheld: "{{by}} kept the class {{cls}}",
      Proposed: "{{by}} (Net0) proposed {{cls}}",
    },
    classify: {
      action: "Classify the defect",
      change: "Change the class",
      title: "The Department's class of the defect",
      hint: "Critical, major or minor as the substantive review defines them (condition 1.7). The class applies at once, including while Net0 disagrees with it.",
      class: "Class",
      area: "An area that admits no error",
      areaHint: "Notification thresholds, statutory deadlines and fees admit no error (condition 1.8): any defect in them keeps the section from acceptance.",
      noArea: "None of these",
      reasons: "Reasons",
      submit: "Record the class",
      saved: "Class recorded.",
    },
    propose: {
      action: "Propose another class",
      title: "Net0's proposal of another class",
      hint: "The Department's class ({{current}}) stays the one that applies until the Department answers the proposal.",
      submit: "Record the proposal",
      saved: "Proposal recorded beside the Department's class.",
    },
    acceptance: {
      title: "Acceptance threshold by section",
      hint: "Condition 1.8: no critical or major defect, at most {{max}} minor defects, every one corrected before adoption, and no error in a threshold, a deadline or a fee. Counted from the defects reported here; acceptance itself is measured by the Department on its reference files.",
      section: "Section",
      noErrorArea: "Threshold, deadline or fee",
      unclassified: "Awaiting class",
      verdict: "Position",
      verdicts: { Met: "Threshold met", NotMet: "Not met", Pending: "Awaiting classification" },
      legend: "Each class reads open defects / all defects reported.",
    },
  },
} as const;
